import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Bookmark, Filter, RefreshCw, EyeOff, Eye, X, MessageCircle, ThumbsUp, ThumbsDown, Smile, Frown, Meh, Trash2, MoreHorizontal, Heart, ChevronDown, Calendar, BarChart3, Facebook } from 'lucide-react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import {
    getAIResponses,
    postAIResponse,
    getComments,
    updatePost,
    updateComment,
    getCommentThread,
    getPostsAnalytics,
    generateAIReply,
    regenerateAIReply,
    deleteAIResponse,
    submitFeedback,
    getAIResponseStatistics,
    addTagsToComment,
    getCommentTags,
    getPortalUsers,
    assignCommentToUser,
    getAssignmentReport
} from '../services/api';

interface AIResponseComment {
    id: number;
    commentId: string;
    commentBody: string;
    postedAt: string;
    username?: string;
    authorUsername?: string;
    authorName?: string;
    socialMediaUserId: string;
    aiResponse: string | null;
    aiResponseEdited: string | null;
    response: string | null;
    isPosted: boolean;
    isHidden: boolean;
    isBookmarked: boolean;
    userFeedback?: 'upvote' | 'downvote' | 'none';
    parentCommentId: string | null;
    post: {
        postId: string;
        postBody: string | null;
        postDescription: string | null;
        imageUrl: string | null;
        videoUrl: string | null;
        platform: string;
        permalink: string | null;
        postOwnerUsername?: string;
    };
    assignedUser?: {
        id: string;
        name?: string;
        username?: string;
        email?: string;
    };
    assignedUserId?: string;
}

interface PostAnalytics {
    id: number;
    postId: string;
    postBody: string | null;
    postedAt: string | null;
    type: string;
    imageUrl: string | null;
    videoUrl: string | null;
    permalink: string | null;
    platform: string;
    isHidden: boolean;
    postedResponse: string | null;
    replyRate: number;
    totalComments: number;
    repliedComments: number;
}

interface StatisticsData {
    yourPosts: number;
    totalComments: number;
    totalRespondableComments: number;
    hiddenComments: number;
    responded: number;
    responsePending: number;
    respondedFromPortal: number;
    respondedFromInstagram: number;
    respondedFromFacebook: number;
    responseRate: number;
}

const AIResponsePage: React.FC = () => {
    const location = useLocation();
    const [comments, setComments] = useState<AIResponseComment[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [platformFilter, setPlatformFilter] = useState<string>('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [total, setTotal] = useState(0);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedPost, setSelectedPost] = useState<AIResponseComment | null>(null);
    const [postComments, setPostComments] = useState<any[]>([]);
    const [loadingComments, setLoadingComments] = useState(false);

    // Edit State
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [editedResponse, setEditedResponse] = useState('');

    // Regenerate Modal State
    const [showRegenerateModal, setShowRegenerateModal] = useState(false);
    const [regenerateCommentId, setRegenerateCommentId] = useState<string | null>(null);
    const [customStyle, setCustomStyle] = useState('');
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [regenToast, setRegenToast] = useState<{ type: 'loading' | 'success' | 'error'; message: string } | null>(null);

    // Filter Modal State
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [filterBookmarked, setFilterBookmarked] = useState<boolean | null>(null); // null = Show All, true = Only Bookmarks
    const [filterPosted, setFilterPosted] = useState<string>('not_posted'); // 'not_posted', 'posted', 'all'
    const [filterHidden, setFilterHidden] = useState<boolean>(false);

    // Sentiment Dropdown in Modal
    const [showModalSentimentDropdown, setShowModalSentimentDropdown] = useState(false);

    // Tag Input in Modal
    const [tagInput, setTagInput] = useState('');
    const [commentTags, setCommentTags] = useState<string[]>([]);

    // New Filters State
    const [filterPostId, setFilterPostId] = useState<string>('');
    const [filterSentiment, setFilterSentiment] = useState<string>('');
    const [filterWorkload, setFilterWorkload] = useState<string>('All Work');
    const [postOptions, setPostOptions] = useState<PostAnalytics[]>([]);
    const [showPostDropdown, setShowPostDropdown] = useState(false);
    const [showSentimentDropdown, setShowSentimentDropdown] = useState(false);
    const [showWorkloadDropdown, setShowWorkloadDropdown] = useState(false);

    // Date Range for User Comments - Default to current date
    const getLocalDateString = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getDefaultDate = () => getLocalDateString(new Date());

    const [filterStartDate, setFilterStartDate] = useState(getDefaultDate());
    const [filterEndDate, setFilterEndDate] = useState(getDefaultDate());

    // Count state for workload display
    const [pendingCount, setPendingCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    // Assignment Modal State
    const [showAssignmentModal, setShowAssignmentModal] = useState(false);
    const [selectedCommentForAssignment, setSelectedCommentForAssignment] = useState<string | null>(null);
    const [assignmentUsers, setAssignmentUsers] = useState<any[]>([]);
    const [loadingAssignmentUsers, setLoadingAssignmentUsers] = useState(false);
    const [assigningUser, setAssigningUser] = useState(false);

    // Tabs
    const tabs = ['User Comments', 'All Posts', 'Statistics'];
    const [activeTab, setActiveTab] = useState('User Comments');

    // Date Range for Statistics - Default to current date
    const [startDate, setStartDate] = useState(getDefaultDate());
    const [endDate, setEndDate] = useState(getDefaultDate());
    const [statisticsData, setStatisticsData] = useState<StatisticsData | null>(null);
    const [statisticsLoading, setStatisticsLoading] = useState(false);

    // Assignment Report states
    const [assignmentScope, setAssignmentScope] = useState<'My Work' | 'All Work'>('All Work');
    const [assignmentReport, setAssignmentReport] = useState<any[]>([]);
    const [assignmentReportLoading, setAssignmentReportLoading] = useState(false);

    // All Posts State
    const [allPosts, setAllPosts] = useState<PostAnalytics[]>([]);
    const [postsLoading, setPostsLoading] = useState(false);
    const [postsPage, setPostsPage] = useState(1);
    const [postsLimit, setPostsLimit] = useState(10);
    const [postsTotal, setPostsTotal] = useState(0);
    const [postsSearchQuery, setPostsSearchQuery] = useState('');
    const [showAdvertisementPosts, setShowAdvertisementPosts] = useState(true);
    const [showHiddenPosts, setShowHiddenPosts] = useState(false);

    // Tagged Posts State
    const [taggedPosts, setTaggedPosts] = useState<PostAnalytics[]>([]);
    const [taggedPostsLoading, setTaggedPostsLoading] = useState(false);
    const [taggedPostsPage, setTaggedPostsPage] = useState(1);
    const [taggedPostsLimit, setTaggedPostsLimit] = useState(10);
    const [taggedPostsTotal, setTaggedPostsTotal] = useState(0);

    // Feedback UI State
    const [feedbackSuccess, setFeedbackSuccess] = useState<{ [key: string]: boolean }>({});
    const [hoveredFeedback, setHoveredFeedback] = useState<{ id: string, type: 'upvote' | 'downvote' } | null>(null);

    // Feedback Form State
    const [feedbackFormCommentId, setFeedbackFormCommentId] = useState<string | null>(null);
    const [feedbackReason, setFeedbackReason] = useState<string | null>(null);
    const [feedbackNote, setFeedbackNote] = useState<string>('');

    const feedbackReasons = ['Inappropriate', 'Outdated copy', 'Not helpful', 'Missing context'];

    // Read platform from URL query parameters - platform is REQUIRED
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const platformParam = searchParams.get('platform');
        if (platformParam) {
            setPlatformFilter(platformParam);
        } else {
            // If no platform specified, default to TIKTOK and update URL
            setPlatformFilter('TIKTOK');
            window.history.replaceState(null, '', '/ai-responses?platform=TIKTOK');
        }
    }, [location.search]);

    useEffect(() => {
        setPage(1);
        setFilterPostId(''); // Clear selected post when switching platforms
        fetchComments();
        if (platformFilter) {
            fetchPostOptions();
        }
    }, [platformFilter]);

    useEffect(() => {
        // Close dropdowns when clicking outside
        const handleClickOutside = () => {
            setShowPostDropdown(false);
            setShowSentimentDropdown(false);
            setShowWorkloadDropdown(false);
            setShowModalSentimentDropdown(false);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const fetchPostOptions = async () => {
        try {
            const response = await getPostsAnalytics({
                platform: platformFilter,
                show_hidden: false, // Exclude hidden posts from filter dropdown
                limit: 50
            });
            setPostOptions(response.items);
        } catch (error) {
            console.error('Failed to fetch post options', error);
        }
    };

    const fetchAssignmentUsers = async () => {
        try {
            setLoadingAssignmentUsers(true);
            const response = await getPortalUsers({ tenant_id: 2 });
            // Handle different response structures - could be array directly or nested in response
            const users = Array.isArray(response) ? response : (response?.users || response?.data || []);
            setAssignmentUsers(users);
        } catch (error) {
            console.error('Failed to fetch assignment users', error);
            setAssignmentUsers([]);
        } finally {
            setLoadingAssignmentUsers(false);
        }
    };

    const handleAssignUser = async (userId?: string) => {
        if (!selectedCommentForAssignment) return;

        try {
            setAssigningUser(true);
            await assignCommentToUser(selectedCommentForAssignment, userId);

            // Update the comment in the local state to reflect the assignment
            setComments(prev => prev.map(comment => {
                if (comment.commentId === selectedCommentForAssignment) {
                    return {
                        ...comment,
                        assignedUserId: userId || undefined,
                        assignedUser: userId ? assignmentUsers.find(u => u.id === userId) : undefined
                    };
                }
                return comment;
            }));

            // Close modal and show success
            setShowAssignmentModal(false);
            setSelectedCommentForAssignment(null);

        } catch (error) {
            console.error('Failed to assign user:', error);
            // You could add a toast notification here for error feedback
        } finally {
            setAssigningUser(false);
        }
    };

    // Fetch posts when All Posts tab is active
    useEffect(() => {
        if (activeTab === 'All Posts' && platformFilter) {
            fetchAllPosts();
        }
    }, [activeTab, postsPage, postsLimit, platformFilter]);

    // Fetch tagged posts when Tagged Posts tab is active
    useEffect(() => {
        if (activeTab === 'Tagged Posts') {
            fetchTaggedPosts();
        }
    }, [activeTab, taggedPostsPage, taggedPostsLimit]);

    useEffect(() => {
        if (activeTab === 'Statistics' && platformFilter) {
            fetchStatistics();
            fetchAssignmentReport();
        }
    }, [activeTab, platformFilter, startDate, endDate, assignmentScope]);

    const fetchAllPosts = async () => {
        try {
            setPostsLoading(true);
            const skip = (postsPage - 1) * postsLimit;
            const params: any = {
                skip,
                limit: postsLimit,
                platform: platformFilter
            };
            if (postsSearchQuery) params.search_query = postsSearchQuery;

            // Apply show_hidden filter
            params.show_hidden = showHiddenPosts;

            const response = await getPostsAnalytics(params);
            setAllPosts(response.items);
            setPostsTotal(response.total);
        } catch (error) {
            console.error('Failed to fetch posts', error);
        } finally {
            setPostsLoading(false);
        }
    };

    const fetchTaggedPosts = async () => {
        try {
            setTaggedPostsLoading(true);
            const skip = (taggedPostsPage - 1) * taggedPostsLimit;
            const params: any = {
                skip,
                limit: taggedPostsLimit,
                platform: 'INSTAGRAM_TAG'
            };

            const response = await getPostsAnalytics(params);
            setTaggedPosts(response.items);
            setTaggedPostsTotal(response.total);
        } catch (error) {
            console.error('Failed to fetch tagged posts', error);
        } finally {
            setTaggedPostsLoading(false);
        }
    };

    const fetchStatistics = async () => {
        try {
            setStatisticsLoading(true);
            const params: any = {
                platform: platformFilter,
                scope: assignmentScope
            };
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            params.timezone_offset = new Date().getTimezoneOffset();

            const response = await getAIResponseStatistics(params);

            // Map API response to frontend StatisticsData structure
            setStatisticsData({
                yourPosts: response.posts_and_comments.your_posts,
                totalComments: response.posts_and_comments.total_comments,
                totalRespondableComments: response.posts_and_comments.total_respondable_comments,
                hiddenComments: response.posts_and_comments.hidden_comments,
                responded: response.comment_responses.responded,
                responsePending: response.comment_responses.response_pending,
                respondedFromPortal: response.comment_responses.responded_from_portal,
                respondedFromInstagram: response.comment_responses.responded_from_platform,
                respondedFromFacebook: response.comment_responses.responded_from_platform || 0,
                responseRate: response.response_rate
            });
        } catch (error) {
            console.error('Failed to fetch statistics', error);
            // Set to zeros on error
            setStatisticsData({
                yourPosts: 0,
                totalComments: 0,
                totalRespondableComments: 0,
                hiddenComments: 0,
                responded: 0,
                responsePending: 0,
                respondedFromPortal: 0,
                respondedFromInstagram: 0,
                respondedFromFacebook: 0,
                responseRate: 0
            });
        } finally {
            setStatisticsLoading(false);
        }
    };

    const fetchAssignmentReport = async () => {
        try {
            setAssignmentReportLoading(true);
            const params: any = {
                platform: platformFilter,
                scope: assignmentScope,
                timezone_offset: new Date().getTimezoneOffset()
            };
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const response = await getAssignmentReport(params);
            setAssignmentReport(response.items || []);
        } catch (error) {
            console.error('Failed to fetch assignment report', error);
            setAssignmentReport([]);
        } finally {
            setAssignmentReportLoading(false);
        }
    };

    useEffect(() => {
        fetchComments();
    }, [page, filterPostId, filterSentiment, filterWorkload, filterStartDate, filterEndDate]);

    useEffect(() => {
        setPage(1);
        fetchComments();
    }, [limit]);

    const fetchComments = async () => {
        try {
            // Don't fetch if platform is not set
            if (!platformFilter) {
                return;
            }

            setLoading(true);

            const skip = (page - 1) * limit;
            const params: any = {
                skip,
                limit: limit,
                platform: platformFilter  // Platform is ALWAYS required
            };

            if (searchQuery) params.search_query = searchQuery;
            if (filterPostId) params.post_id = filterPostId;
            if (filterSentiment) params.sentiment = filterSentiment;
            if (filterWorkload) params.workload = filterWorkload;
            if (filterStartDate) params.start_date = filterStartDate;
            if (filterEndDate) params.end_date = filterEndDate;
            params.timezone_offset = new Date().getTimezoneOffset(); // Send local timezone offset to backend 
            if (filterBookmarked !== null) params.bookmarked = filterBookmarked;
            if (filterPosted !== 'all') params.posted = filterPosted === 'posted';
            // Always send show_hidden parameter so backend knows user's intent
            params.show_hidden = filterHidden;

            const response = await getAIResponses(params);

            // Map the response to include assignment data if it exists
            const mappedComments = response.items.map((comment: any) => ({
                ...comment,
                assignedUserId: comment.assignedUserId,
                assignedUser: comment.assignedUser
            }));

            setComments(mappedComments);
            setTotal(response.total);

            // Update counts for workload display
            setPendingCount(response.pending_count || 0);
            setTotalCount(response.total_parent_comments || response.total || 0);
        } catch (error) {
            console.error('Failed to fetch AI responses', error);
            setComments([]);
            setTotal(0);
            setPendingCount(0);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    const flattenComments = (nodes: any[]): any[] => {
        let flat: any[] = [];
        nodes.forEach(node => {
            const { replies, ...rest } = node;
            flat.push(rest);
            if (replies && replies.length > 0) {
                flat = flat.concat(flattenComments(replies));
            }
        });
        return flat;
    };

    const fetchPostComments = async (postId: string, commentId?: string) => {
        try {
            setLoadingComments(true);
            let data;
            if (commentId) {
                const thread = await getCommentThread(postId, commentId);
                data = flattenComments(thread);
            } else {
                const response = await getComments(postId);
                data = response.items;
            }
            setPostComments(data);
        } catch (error) {
            console.error('Failed to fetch post comments', error);
        } finally {
            setLoadingComments(false);
        }
    };

    const handleRowClick = (comment: AIResponseComment) => {
        setSelectedPost(comment);
        setShowModal(true);
        fetchPostComments(comment.post.postId, comment.commentId);
        // Load tags for this comment
        if (comment.commentId) {
            loadCommentTags(comment.commentId);
        }
    };

    const handlePostClick = (post: PostAnalytics) => {
        // Convert PostAnalytics to AIResponseComment format for modal display
        const postAsComment: AIResponseComment = {
            id: 0,
            commentId: '', // No specific comment selected
            socialMediaUserId: '',
            commentBody: '',
            parentCommentId: null,
            postedAt: post.postedAt || '',
            username: '',
            isHidden: post.isHidden, // Use actual isHidden value from post
            isBookmarked: false,
            aiResponse: null,
            aiResponseEdited: null,
            response: null,
            isPosted: false,
            userFeedback: 'none',
            post: {
                postId: post.postId,
                postBody: post.postBody || '',
                postDescription: null,
                imageUrl: post.imageUrl,
                videoUrl: post.videoUrl,
                permalink: post.permalink,
                platform: post.platform
            }
        };
        setSelectedPost(postAsComment);
        setShowModal(true);
        // Fetch all comments for this post (no specific comment selected)
        fetchPostComments(post.postId, '');
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedPost(null);
        setPostComments([]);
        setEditingCommentId(null);
        setEditedResponse('');
        setCommentTags([]);
        setTagInput('');
    };

    const handleSearch = () => {
        setPage(1);
        fetchComments();
    };

    const handlePostResponse = async (commentId: string, e?: React.MouseEvent) => {
        if (e) e.stopPropagation(); // Prevent row click
        try {
            await postAIResponse(commentId);

            // Remove posted comment from the list (since it's now posted and handled)
            setComments(prev => prev.filter(c => c.commentId !== commentId));

            // Decrement pending count
            setPendingCount(prev => Math.max(0, prev - 1));

            // Update local state in modal comments
            setPostComments(prev => prev.map(c => {
                if (c.commentId === commentId) {
                    return {
                        ...c,
                        isPosted: true,
                        response: c.aiResponseEdited || c.aiResponse
                    };
                }
                return c;
            }));

            // Close modal if the posted comment was the selected one
            if (selectedPost && selectedPost.commentId === commentId) {
                handleCloseModal();
            }

        } catch (error) {
            console.error('Failed to post response', error);
            alert('Failed to post response');
        }
    };

    const handleStartEdit = (comment: any) => {
        setEditingCommentId(comment.commentId);
        // Check for valid response (not null, not "0", not empty, length > 1)
        const isValid = (val: any): val is string => typeof val === 'string' && val.length > 1 && val !== '0' && val !== 'null';
        const response = isValid(comment.aiResponseEdited) ? comment.aiResponseEdited :
            isValid(comment.aiResponse) ? comment.aiResponse : '';
        setEditedResponse(response);
    };

    const handleCancelEdit = () => {
        setEditingCommentId(null);
        setEditedResponse('');
    };

    const handleSaveEdit = async (commentId: string) => {
        try {
            await updateComment(commentId, { aiResponseEdited: editedResponse });

            // Update local state
            setPostComments(prev => prev.map(c => {
                if (c.commentId === commentId) {
                    return {
                        ...c,
                        aiResponseEdited: editedResponse
                    };
                }
                return c;
            }));

            handleCancelEdit();
        } catch (error) {
            console.error('Failed to save edit', error);
            alert('Failed to save edit');
        }
    };

    const handleRegenerateClick = (commentId: string) => {
        setRegenerateCommentId(commentId);
        setShowRegenerateModal(true);
        setCustomStyle('');
    };

    const handleSentimentChange = async (commentId: string, newSentiment: string) => {
        try {
            await updateComment(commentId, { aiSentimentEdited: newSentiment });

            // Update local state
            setPostComments(prev => prev.map(c => {
                if (c.commentId === commentId) {
                    return {
                        ...c,
                        aiSentimentEdited: newSentiment
                    };
                }
                return c;
            }));

            setShowModalSentimentDropdown(false);
        } catch (error) {
            console.error('Failed to update sentiment', error);
            alert('Failed to update sentiment');
        }
    };

    const loadCommentTags = async (commentId: string) => {
        try {
            const response = await getCommentTags(commentId);
            setCommentTags(response.tags || []);
        } catch (error) {
            console.error('Failed to load tags', error);
            setCommentTags([]);
        }
    };

    const saveCommentTags = async (commentId: string, tags: string[]) => {
        try {
            await addTagsToComment(commentId, tags);
        } catch (error) {
            console.error('Failed to save tags', error);
            alert('Failed to save tags');
        }
    };

    const handleTagInputKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>, commentId: string) => {
        if (e.key === 'Enter' && tagInput.trim()) {
            e.preventDefault();
            const newTags = [...commentTags, tagInput.trim()];
            setCommentTags(newTags);
            setTagInput('');
            await saveCommentTags(commentId, newTags);
        }
    };

    const handleTagInputBlur = async (commentId: string) => {
        if (tagInput.trim()) {
            const newTags = [...commentTags, tagInput.trim()];
            setCommentTags(newTags);
            setTagInput('');
            await saveCommentTags(commentId, newTags);
        }
    };

    const handleRemoveTag = async (tagToRemove: string, commentId: string) => {
        const newTags = commentTags.filter(tag => tag !== tagToRemove);
        setCommentTags(newTags);
        await saveCommentTags(commentId, newTags);
    };

    // Initial AI generation (no popup, default style – uses same backend as regeneration)
    const handleGenerateInitial = async (commentId: string) => {
        setIsRegenerating(true);
        setRegenToast({ type: 'loading', message: 'Generating AI response…' });

        try {
            // Use regenerate endpoint without a specific style so we get the full Claude pipeline,
            // but from a UX perspective this is treated as the first-time "Generate".
            const response = await regenerateAIReply(commentId);
            const newResponse = response.new_response || response.draft || response.aiResponse || response.response;

            setComments(prev => prev.map(c => {
                if (c.commentId === commentId) {
                    return {
                        ...c,
                        aiResponse: newResponse,
                        isNeedAIReply: false
                    };
                }
                return c;
            }));

            if (selectedPost && selectedPost.commentId === commentId) {
                setSelectedPost(prev => prev ? {
                    ...prev,
                    aiResponse: newResponse,
                    isNeedAIReply: false
                } : null);
            }

            setPostComments(prev => prev.map(c => {
                if (c.commentId === commentId) {
                    return {
                        ...c,
                        aiResponse: newResponse,
                        aiResponseEdited: null,
                        response: newResponse,
                        isNeedAIReply: false
                    };
                }
                return c;
            }));

            setRegenToast({ type: 'success', message: 'AI response generated successfully.' });
            setTimeout(() => {
                setRegenToast((current) => (current?.type === 'success' ? null : current));
            }, 3000);
        } catch (error) {
            console.error('Failed to generate AI response', error);
            setRegenToast({ type: 'error', message: 'Failed to generate AI response. Please try again.' });
            setTimeout(() => {
                setRegenToast((current) => (current?.type === 'error' ? null : current));
            }, 4000);
        } finally {
            setIsRegenerating(false);
        }
    };

    const handleRegenerate = async (style?: string) => {
        const commentId = regenerateCommentId || selectedPost?.commentId;
        if (!commentId) return;

        // Close modal
        setShowRegenerateModal(false);
        setRegenerateCommentId(null);
        setCustomStyle('');
        setIsRegenerating(true);
        setRegenToast({ type: 'loading', message: 'Generating AI response…' });

        try {
            // Call the regenerate endpoint with style
            const response = await regenerateAIReply(commentId, style);

            // Update local state with new response
            const newResponse = response.new_response || response.draft;

            console.log('Regenerate response:', response);
            console.log('New AI response:', newResponse);
            console.log('Style used:', style);

            setComments(prev => prev.map(c => {
                if (c.commentId === commentId) {
                    return {
                        ...c,
                        aiResponse: newResponse,
                        isNeedAIReply: false
                    };
                }
                return c;
            }));

            // Update modal state - update both selectedPost and postComments
            if (selectedPost && selectedPost.commentId === commentId) {
                setSelectedPost(prev => prev ? {
                    ...prev,
                    aiResponse: newResponse,
                    isNeedAIReply: false
                } : null);
            }

            setPostComments(prev => {
                const updated = prev.map(c => {
                    if (c.commentId === commentId) {
                        console.log('Updating comment in postComments:', c.commentId, 'with new response:', newResponse);
                        return {
                            ...c,
                            aiResponse: newResponse,
                            aiResponseEdited: null,
                            response: newResponse,
                            isNeedAIReply: false
                        };
                    }
                    return c;
                });
                console.log('Updated postComments:', updated);
                return updated;
            });

            setRegenToast({ type: 'success', message: 'AI response regenerated successfully.' });
            setTimeout(() => {
                setRegenToast((current) => (current?.type === 'success' ? null : current));
            }, 3000);

        } catch (error) {
            console.error('Failed to regenerate response', error);
            setRegenToast({ type: 'error', message: 'Failed to regenerate AI response. Please try again.' });
            setTimeout(() => {
                setRegenToast((current) => (current?.type === 'error' ? null : current));
            }, 4000);
        } finally {
            setIsRegenerating(false);
        }
    };

    const truncateText = (text: string | null, maxLength: number = 80) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        let safeDateString = dateString;
        if (!safeDateString.endsWith('Z') && !safeDateString.includes('+') && !safeDateString.match(/-\d{2}:\d{2}$/)) {
            safeDateString = safeDateString.replace(' ', 'T') + 'Z';
        }

        const date = new Date(safeDateString);
        const formattedDate = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'UTC',
            timeZoneName: 'short'
        });

        const now = new Date();
        const diffInSeconds = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 1000));

        let timeAgo = '';
        if (diffInSeconds < 60) {
            timeAgo = `${diffInSeconds}s`;
        } else if (diffInSeconds < 3600) {
            timeAgo = `${Math.floor(diffInSeconds / 60)}m`;
        } else if (diffInSeconds < 86400) {
            timeAgo = `${Math.floor(diffInSeconds / 3600)}h`;
        } else {
            const totalDays = Math.floor(diffInSeconds / 86400);
            if (totalDays < 7) {
                timeAgo = `${totalDays}d`;
            } else {
                const weeks = Math.floor(totalDays / 7);
                const remainingDays = totalDays % 7;
                if (remainingDays === 0) {
                    timeAgo = `${weeks}w`;
                } else {
                    timeAgo = `${weeks}w ${remainingDays}d`;
                }
            }
        }

        return `${formattedDate} - ${timeAgo}`;
    };

    const formatFullTimestamp = (dateString: string) => {
        if (!dateString) return '';
        try {
            let safeDateString = dateString;
            if (!safeDateString.endsWith('Z') && !safeDateString.includes('+') && !safeDateString.match(/-\d{2}:\d{2}$/)) {
                safeDateString = safeDateString.replace(' ', 'T') + 'Z';
            }

            const date = new Date(safeDateString);
            const formattedDate = date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'UTC',
                timeZoneName: 'short'
            });

            const now = new Date();
            const diffInSeconds = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 1000));

            let timeAgo = '';
            if (diffInSeconds < 60) {
                timeAgo = `${diffInSeconds}s`;
            } else if (diffInSeconds < 3600) {
                timeAgo = `${Math.floor(diffInSeconds / 60)}m`;
            } else if (diffInSeconds < 86400) {
                timeAgo = `${Math.floor(diffInSeconds / 3600)}h`;
            } else {
                const totalDays = Math.floor(diffInSeconds / 86400);
                if (totalDays < 7) {
                    timeAgo = `${totalDays}d`;
                } else {
                    const weeks = Math.floor(totalDays / 7);
                    const remainingDays = totalDays % 7;
                    if (remainingDays === 0) {
                        timeAgo = `${weeks}w`;
                    } else {
                        timeAgo = `${weeks}w ${remainingDays}d`;
                    }
                }
            }

            return `${formattedDate} - ${timeAgo}`;
        } catch (e) {
            return dateString;
        }
    };

    const handleDeleteResponse = async (commentId: string) => {
        if (window.confirm('Are you sure you want to delete this response?')) {
            try {
                await deleteAIResponse(commentId);

                // If filtering by 'posted', remove the comment from the list since it's no longer posted
                // Otherwise, update it to show as not posted
                if (filterPosted === 'posted') {
                    setComments(prev => prev.filter(c => c.commentId !== commentId));
                } else {
                    setComments(prev => prev.map(c => {
                        if (c.commentId === commentId) {
                            return {
                                ...c,
                                isPosted: false,
                                response: null
                            };
                        }
                        return c;
                    }));
                }

                // Update local state in modal comments
                setPostComments(prev => {
                    const postOwnerUsername = selectedPost?.post?.postOwnerUsername;

                    // Update parent
                    const updated = prev.map(c => {
                        if (c.commentId === commentId) {
                            return { ...c, isPosted: false, response: null };
                        }
                        return c;
                    });

                    // Remove the owner reply child (check both socialmind_owner and actual post owner)
                    return updated.filter(c =>
                        !(c.parentCommentId === commentId &&
                            (c.socialMediaUserId === 'socialmind_owner' ||
                                c.username === 'socialmind_owner' ||
                                c.authorUsername === 'socialmind_owner' ||
                                (postOwnerUsername && (
                                    c.username === postOwnerUsername ||
                                    c.authorUsername === postOwnerUsername
                                ))))
                    );
                });

                // Update selectedPost
                if (selectedPost && selectedPost.commentId === commentId) {
                    setSelectedPost(prev => prev ? { ...prev, isPosted: false, response: null } : null);
                }

                // Close modal if filtering by posted and the deleted comment was selected
                if (filterPosted === 'posted' && selectedPost && selectedPost.commentId === commentId) {
                    handleCloseModal();
                }

            } catch (error) {
                console.error('Failed to delete response', error);
                alert('Failed to delete response');
            }
        }
    };

    const handleToggleHide = async (comment: AIResponseComment) => {
        if (!comment) return;
        try {
            const newHiddenState = !comment.isHidden;

            // Optimistic update for modal
            setSelectedPost(prev => prev ? { ...prev, isHidden: newHiddenState } : null);

            // Check if this is a post (no commentId) or a comment
            const isPost = !comment.commentId;

            if (isPost) {
                // Remove post from the All Posts list immediately
                setAllPosts(prev => prev.filter(p => p.postId !== comment.post.postId));
                setPostsTotal(prev => Math.max(0, prev - 1));

                // Update post via post API
                await updatePost(comment.post.postId, { isHidden: newHiddenState });

                // Refresh User Comments tab to reflect the hidden post's comments being filtered
                // This ensures comments from hidden posts are removed from the User Comments list
                fetchComments();
            } else {
                // Remove from list immediately since it no longer matches the filter
                setComments(prev => prev.filter(c => c.commentId !== comment.commentId));

                // Update total count
                setTotal(prev => Math.max(0, prev - 1));

                // Update comment via comment API
                await updateComment(comment.commentId, { isHidden: newHiddenState } as any);
            }
        } catch (error) {
            console.error('Failed to toggle hide', error);
            // Revert on error
            if (!comment.commentId) {
                // Revert post - add back to the list
                setAllPosts(prev => [...prev, {
                    id: 0,
                    postId: comment.post.postId,
                    postBody: comment.post.postBody,
                    postedAt: null,
                    type: '',
                    imageUrl: comment.post.imageUrl,
                    videoUrl: comment.post.videoUrl,
                    permalink: comment.post.permalink,
                    platform: comment.post.platform,
                    isHidden: comment.isHidden,
                    postedResponse: null,
                    replyRate: 0,
                    totalComments: 0,
                    repliedComments: 0
                }]);
                setPostsTotal(prev => prev + 1);
                setSelectedPost(prev => prev ? { ...prev, isHidden: comment.isHidden } : null);

                // Revert User Comments refresh
                fetchComments();
            } else {
                // Revert comment - add back to the list
                setComments(prev => [...prev, comment]);
                setTotal(prev => prev + 1);
                setSelectedPost(prev => prev ? { ...prev, isHidden: comment.isHidden } : null);
            }
        }
    };

    const handleToggleBookmark = async (comment: AIResponseComment) => {
        if (!comment) return;
        try {
            const newBookmarkedState = !comment.isBookmarked;

            // Optimistic update for modal
            setSelectedPost(prev => prev ? { ...prev, isBookmarked: newBookmarkedState } : null);

            // Optimistic update for list
            setComments(prev => prev.map(c =>
                c.commentId === comment.commentId ? { ...c, isBookmarked: newBookmarkedState } : c
            ));

            await updateComment(comment.commentId, { isBookmarked: newBookmarkedState } as any);
        } catch (error) {
            console.error('Failed to toggle bookmark', error);
            // Revert on error
            setSelectedPost(prev => prev ? { ...prev, isBookmarked: comment.isBookmarked } : null);
            setComments(prev => prev.map(c =>
                c.commentId === comment.commentId ? { ...c, isBookmarked: comment.isBookmarked } : c
            ));
        }
    };

    const handleFeedbackReasonSubmit = async (commentId: string) => {
        try {
            await submitFeedback(commentId, {
                vote_type: 'downvote',
                reason_code: feedbackReason || undefined,
                custom_reason: feedbackNote || undefined
            });

            setFeedbackFormCommentId(null);
            setFeedbackReason(null);
            setFeedbackNote('');

            // Show thank you message
            setFeedbackSuccess(prev => ({ ...prev, [commentId]: true }));
            setTimeout(() => {
                setFeedbackSuccess(prev => {
                    const next = { ...prev };
                    delete next[commentId];
                    return next;
                });
            }, 3000);
        } catch (error) {
            console.error('Failed to submit feedback reason', error);
            alert('Failed to submit feedback');
        }
    };

    const handleFeedback = async (commentId: string, voteType: 'upvote' | 'downvote') => {
        // Find current state to allow toggle off
        const currentComment = comments.find(c => c.commentId === commentId) || postComments.find(c => c.commentId === commentId);
        const currentVote = currentComment?.userFeedback || 'none';

        // If clicking same vote type, toggle to 'none', else set to new vote type
        const newVote = currentVote === voteType ? 'none' : voteType;

        // Manage Feedback Form State
        if (newVote === 'downvote') {
            setFeedbackFormCommentId(commentId);
            setFeedbackReason(null);
            setFeedbackNote('');
        } else {
            setFeedbackFormCommentId(null);
        }

        // Helper to update state
        const updateState = (vote: 'upvote' | 'downvote' | 'none') => {
            // Update list
            setComments(prev => prev.map(c =>
                c.commentId === commentId ? { ...c, userFeedback: vote } : c
            ));

            // Update modal list
            setPostComments(prev => prev.map(c =>
                c.commentId === commentId ? { ...c, userFeedback: vote } : c
            ));

            // Update selected post if it matches
            if (selectedPost?.commentId === commentId) {
                setSelectedPost(prev => prev ? { ...prev, userFeedback: vote } : null);
            }
        };

        try {
            // Optimistic update
            updateState(newVote);

            // Show thank you message if voting (not unvoting)
            // For downvotes, we show it after the reason form is submitted
            if (newVote === 'upvote') {
                setFeedbackSuccess(prev => ({ ...prev, [commentId]: true }));
                setTimeout(() => {
                    setFeedbackSuccess(prev => {
                        const next = { ...prev };
                        delete next[commentId];
                        return next;
                    });
                }, 3000);
            }

            await submitFeedback(commentId, { vote_type: newVote });
        } catch (error) {
            console.error('Failed to submit feedback', error);
            // Revert
            updateState(currentVote as any);
        }
    };

    const buildCommentTree = (comments: any[]) => {
        const map = new Map();
        const roots: any[] = [];

        // Initialize map
        comments.forEach(c => {
            map.set(c.commentId, { ...c, children: [] });
        });

        // Build tree
        comments.forEach(c => {
            if (c.parentCommentId && map.has(c.parentCommentId)) {
                map.get(c.parentCommentId).children.push(map.get(c.commentId));
            } else {
                roots.push(map.get(c.commentId));
            }
        });

        return roots;
    };

    const getCommentUrl = (comment: any) => {
        const permalink = selectedPost?.post?.permalink;
        if (!permalink) return '#';

        const platform = selectedPost?.post?.platform?.toUpperCase();

        // Instagram: Try multiple URL formats for comment navigation
        // Note: Instagram's web interface has limited support for direct comment linking
        if ((platform === 'INSTAGRAM' || platform === 'FACEBOOK')) {
            // Remove trailing slash if present for consistency
            const cleanPermalink = permalink.endsWith('/') ? permalink.slice(0, -1) : permalink;

            // Try format: /p/{media_id}/c/{comment_id}/ (Instagram's internal format)
            // This works better than query parameters
            return `${cleanPermalink}/c/${comment.commentId}/`;
        }

        // TikTok doesn't support direct comment links
        return permalink;
    };

    const renderComment = (comment: any, depth = 0) => {
        const isEditing = editingCommentId === comment.commentId;

        // Get the post owner username to identify owner replies
        const postOwnerUsername = selectedPost?.post?.postOwnerUsername;

        // Check for a child comment that is actually a reply from the brand/post owner
        const brandReplyChild = comment.children?.find((c: any) =>
            c.socialMediaUserId === 'socialmind_owner' ||
            c.username === 'socialmind_owner' ||
            c.authorUsername === 'socialmind_owner' ||
            (postOwnerUsername && (
                c.username === postOwnerUsername ||
                c.authorUsername === postOwnerUsername
            ))
        );

        // Determine effective response content and state
        const hasPostedResponse = comment.isPosted || !!brandReplyChild;

        // Get the AI response text - prioritize actual AI response over child comment body
        // Helper to check if a value is a valid response string (not null, not "0", not empty)
        const isValidResponse = (val: any): val is string => {
            return typeof val === 'string' && val.length > 1 && val !== '0' && val !== 'null';
        };

        const getResponseText = () => {
            if (isEditing) return editedResponse;
            // Only use brandReplyChild.commentBody if it's a valid non-empty string response
            if (isValidResponse(brandReplyChild?.commentBody)) {
                return brandReplyChild.commentBody;
            }
            // Fall back to AI response fields - check each for valid content
            if (isValidResponse(comment.aiResponseEdited)) return comment.aiResponseEdited;
            if (isValidResponse(comment.aiResponse)) return comment.aiResponse;
            if (isValidResponse(comment.response)) return comment.response;
            return '';
        };
        const responseText = getResponseText();
        const responseTimestamp = brandReplyChild?.postedAt || (comment.isPosted ? new Date().toISOString() : null); // Fallback to now if posted but no child yet (shouldn't happen often)

        // Always show the replies block so users can draft/generate a response
        const showRepliesBlock = true;

        return (
            <div key={`${comment.commentId}-${comment.aiResponse?.substring(0, 20) || 'no-response'}`} style={{ marginLeft: depth > 0 ? '2rem' : '0', marginBottom: '2rem' }}>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    {/* Avatar */}
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#f1f5f9', flexShrink: 0, overflow: 'hidden' }}>
                        <img
                            src={`https://ui-avatars.com/api/?name=${comment.authorUsername || comment.username || comment.socialMediaUserId}&background=random`}
                            alt="User"
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>

                    {/* Comment Content */}
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 'fit-content' }}>
                                <span style={{ fontWeight: 600, fontSize: '0.95rem', color: '#0f172a' }}>{comment.authorUsername || comment.username || comment.socialMediaUserId}</span>
                                <a href={getCommentUrl(comment)} target="_blank" rel="noopener noreferrer" style={{ color: '#64748b', textDecoration: 'none', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                                    {selectedPost?.post?.platform?.toUpperCase() === 'TIKTOK' ? (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                        </svg>
                                    ) : selectedPost?.post?.platform?.toUpperCase() === 'TWITTER' || selectedPost?.post?.platform?.toUpperCase() === 'X' ? (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                        </svg>
                                    ) : selectedPost?.post?.platform?.toUpperCase() === 'FACEBOOK' ? (
                                        <Facebook size={14} style={{ flexShrink: 0 }} />
                                    ) : (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.645.069-4.849.069-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                        </svg>
                                    )}
                                    View
                                </a>
                            </div>

                            {/* Sentiment & Tags */}
                            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                <div style={{ position: 'relative' }}>
                                    <button
                                        className="icon-btn"
                                        style={{
                                            border: '1px solid #e2e8f0',
                                            padding: '0.25rem 0.5rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.25rem',
                                            fontSize: '0.75rem',
                                            color: '#64748b'
                                        }}
                                        onClick={(e) => { e.stopPropagation(); setShowModalSentimentDropdown(!showModalSentimentDropdown); }}
                                    >
                                        {(() => {
                                            const displaySentiment = comment.aiSentimentEdited || comment.aiSentiment;
                                            return (
                                                <>
                                                    <span>Sentiment: {displaySentiment === 'POSITIVE' ? 'Positive' : displaySentiment === 'NEGATIVE' ? 'Negative' : 'Neutral'}</span>
                                                    {displaySentiment === 'POSITIVE' ? <Smile size={18} color="#4CAF50" /> : displaySentiment === 'NEGATIVE' ? <Frown size={18} color="#F44336" /> : <Meh size={18} color="#9C27B0" />}
                                                    <ChevronDown size={14} color="#94a3b8" />
                                                </>
                                            );
                                        })()}
                                    </button>

                                    {showModalSentimentDropdown && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            marginTop: '0.25rem',
                                            backgroundColor: 'white',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '0.5rem',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                            zIndex: 1000,
                                            minWidth: '180px'
                                        }}>
                                            <div
                                                onClick={() => handleSentimentChange(comment.commentId, 'POSITIVE')}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    fontSize: '0.875rem',
                                                    borderBottom: '1px solid #f1f5f9'
                                                }}
                                            >
                                                <Smile size={16} color="#4CAF50" /> Positive
                                            </div>
                                            <div
                                                onClick={() => handleSentimentChange(comment.commentId, 'NEGATIVE')}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    fontSize: '0.875rem',
                                                    borderBottom: '1px solid #f1f5f9'
                                                }}
                                            >
                                                <Frown size={16} color="#F44336" /> Negative
                                            </div>
                                            <div
                                                onClick={() => handleSentimentChange(comment.commentId, 'NEUTRAL')}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                <Meh size={16} color="#9C27B0" /> Neutral
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                    {commentTags.map((tag, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.25rem',
                                                padding: '0.25rem 0.5rem',
                                                backgroundColor: '#f1f5f9',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '9999px',
                                                fontSize: '0.75rem',
                                                color: '#475569'
                                            }}
                                        >
                                            {tag}
                                            <X
                                                size={12}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleRemoveTag(tag, comment.commentId)}
                                            />
                                        </div>
                                    ))}
                                    <input
                                        type="text"
                                        placeholder="Add tag..."
                                        value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyDown={(e) => handleTagInputKeyDown(e, comment.commentId)}
                                        onBlur={() => handleTagInputBlur(comment.commentId)}
                                        style={{
                                            padding: '0.35rem 0.75rem',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '9999px',
                                            fontSize: '0.875rem',
                                            width: '140px',
                                            outline: 'none',
                                            minWidth: '100px'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <p style={{ fontSize: '0.95rem', color: '#334155', marginBottom: '0.5rem', lineHeight: '1.5' }}>
                            {comment.commentBody}
                        </p>

                        <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '1rem' }}>
                            {formatFullTimestamp(comment.postedAt)}
                        </div>

                        {/* AI Response Section (Replies) */}
                        {showRepliesBlock && (
                            <div style={{ marginTop: '2rem' }}>
                                <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '1.5rem', color: '#0f172a' }}>Replies</h3>

                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    {/* Brand Avatar */}
                                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'black', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '0.8rem', overflow: 'hidden' }}>
                                        {selectedPost?.post?.postOwnerUsername?.substring(0, 3).toUpperCase() || 'BRA'}
                                    </div>

                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <span style={{ fontWeight: 600, fontSize: '0.95rem', color: '#0f172a' }}>{selectedPost?.post?.postOwnerUsername || 'Brand'}</span>
                                                {hasPostedResponse && (
                                                    <span style={{
                                                        fontSize: '0.75rem',
                                                        color: '#64748b',
                                                        backgroundColor: '#f1f5f9',
                                                        padding: '0.1rem 0.4rem',
                                                        borderRadius: '4px',
                                                        fontWeight: 500
                                                    }}>
                                                        Posted
                                                    </span>
                                                )}
                                            </div>

                                            {!hasPostedResponse ? (
                                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                    {!isEditing ? (
                                                        <>
                                                            <button
                                                                onClick={() => {
                                                                    if (isRegenerating) return;
                                                                    if (responseText) {
                                                                        // Existing AI response → open style popup for regeneration
                                                                        handleRegenerateClick(comment.commentId);
                                                                    } else {
                                                                        // First time generation → default style, no popup
                                                                        handleGenerateInitial(comment.commentId);
                                                                    }
                                                                }}
                                                                disabled={isRegenerating}
                                                                style={{
                                                                    padding: '0.35rem 1rem',
                                                                    border: '1px solid #e2e8f0',
                                                                    borderRadius: '9999px',
                                                                    backgroundColor: 'white',
                                                                    cursor: isRegenerating ? 'wait' : 'pointer',
                                                                    color: '#0f172a',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 700,
                                                                    textTransform: 'uppercase',
                                                                    opacity: isRegenerating ? 0.7 : 1,
                                                                    gap: '0.35rem',
                                                                    minWidth: '120px'
                                                                }}
                                                            >
                                                                {isRegenerating && (
                                                                    <span
                                                                        style={{
                                                                            width: '12px',
                                                                            height: '12px',
                                                                            borderRadius: '9999px',
                                                                            border: '2px solid #cbd5e1',
                                                                            borderTopColor: '#0f172a',
                                                                            animation: 'spin 0.8s linear infinite',
                                                                            display: 'inline-block'
                                                                        }}
                                                                    />
                                                                )}
                                                                <span>
                                                                    {isRegenerating
                                                                        ? 'WORKING...'
                                                                        : responseText
                                                                            ? 'REGENERATE'
                                                                            : 'GENERATE'}
                                                                </span>
                                                            </button>
                                                            <button
                                                                onClick={() => handleStartEdit(comment)}
                                                                style={{
                                                                    padding: '0.35rem 1rem',
                                                                    border: '1px solid #e2e8f0',
                                                                    borderRadius: '9999px',
                                                                    backgroundColor: 'white',
                                                                    cursor: 'pointer',
                                                                    color: '#0f172a',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 700,
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >
                                                                EDIT
                                                            </button>
                                                            <button
                                                                onClick={(e) => handlePostResponse(comment.commentId, e)}
                                                                disabled={!responseText}
                                                                style={{
                                                                    backgroundColor: !responseText ? '#94a3b8' : 'black',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '9999px',
                                                                    padding: '0.35rem 1rem',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 700,
                                                                    cursor: !responseText ? 'not-allowed' : 'pointer',
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >
                                                                POST
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', display: 'flex', alignItems: 'center' }}
                                                                title="Insert Emoji"
                                                            >
                                                                <Smile size={20} color="#64748b" />
                                                            </button>
                                                            <button
                                                                onClick={handleCancelEdit}
                                                                style={{
                                                                    padding: '0.35rem 1rem',
                                                                    border: '1px solid #e2e8f0',
                                                                    borderRadius: '9999px',
                                                                    backgroundColor: 'white',
                                                                    cursor: 'pointer',
                                                                    color: '#64748b',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 700,
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >
                                                                CANCEL
                                                            </button>
                                                            <button
                                                                onClick={() => handleSaveEdit(comment.commentId)}
                                                                style={{
                                                                    padding: '0.35rem 1rem',
                                                                    border: '1px solid #e2e8f0',
                                                                    borderRadius: '9999px',
                                                                    backgroundColor: 'white', // Per screenshot, looks like white button
                                                                    cursor: 'pointer',
                                                                    color: '#0f172a',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 700,
                                                                    textTransform: 'uppercase'
                                                                }}
                                                            >
                                                                SAVE
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => handleDeleteResponse(comment.commentId)}
                                                    style={{
                                                        backgroundColor: 'black',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '9999px',
                                                        padding: '0.35rem 1rem',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 700,
                                                        cursor: 'pointer',
                                                        textTransform: 'uppercase'
                                                    }}
                                                >
                                                    DELETE
                                                </button>
                                            )}
                                        </div>

                                        <div style={{ fontSize: '0.95rem', color: '#334155', marginBottom: '0.5rem', lineHeight: '1.5' }}>
                                            <span style={{ color: '#2563eb', marginRight: '0.25rem', fontWeight: 500 }}>@{comment.authorUsername || comment.username || comment.socialMediaUserId}</span>
                                            {isEditing ? (
                                                <div style={{ width: '100%' }}>
                                                    <textarea
                                                        value={editedResponse}
                                                        onChange={(e) => setEditedResponse(e.target.value.slice(0, 2200))}
                                                        style={{
                                                            width: '100%',
                                                            padding: '0.75rem',
                                                            marginTop: '0.5rem',
                                                            borderRadius: '8px',
                                                            border: '1px solid #e2e8f0',
                                                            fontFamily: 'inherit',
                                                            lineHeight: '1.5',
                                                            resize: 'vertical',
                                                            minHeight: '80px'
                                                        }}
                                                        rows={4}
                                                    />
                                                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                                                        {editedResponse.length}/2200 characters
                                                    </div>
                                                </div>
                                            ) : (
                                                <span>{responseText}</span>
                                            )}
                                        </div>

                                        <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '1rem' }}>
                                            {hasPostedResponse ? (responseTimestamp ? formatFullTimestamp(responseTimestamp) : 'Posted') : 'Not posted yet'}
                                        </div>

                                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                            <div style={{ position: 'relative' }}>
                                                {hoveredFeedback?.id === comment.commentId && hoveredFeedback?.type === 'upvote' && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        bottom: '100%',
                                                        left: '50%',
                                                        transform: 'translateX(-50%)',
                                                        marginBottom: '8px',
                                                        backgroundColor: '#1e293b',
                                                        color: 'white',
                                                        padding: '6px 10px',
                                                        borderRadius: '6px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500,
                                                        whiteSpace: 'nowrap',
                                                        zIndex: 10,
                                                        pointerEvents: 'none',
                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                                    }}>
                                                        Good response
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '100%',
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            borderLeft: '5px solid transparent',
                                                            borderRight: '5px solid transparent',
                                                            borderTop: '5px solid #1e293b'
                                                        }}></div>
                                                    </div>
                                                )}
                                                <button
                                                    className="icon-btn"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                                    onClick={() => handleFeedback(comment.commentId, 'upvote')}
                                                    onMouseEnter={() => setHoveredFeedback({ id: comment.commentId, type: 'upvote' })}
                                                    onMouseLeave={() => setHoveredFeedback(null)}
                                                >
                                                    <ThumbsUp
                                                        size={16}
                                                        color={comment.userFeedback === 'upvote' ? '#2563eb' : "#94a3b8"}
                                                        fill={comment.userFeedback === 'upvote' ? '#2563eb' : 'none'}
                                                    />
                                                </button>
                                            </div>
                                            <button
                                                className="icon-btn"
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                                onClick={() => handleFeedback(comment.commentId, 'downvote')}
                                            >
                                                <ThumbsDown
                                                    size={16}
                                                    color={comment.userFeedback === 'downvote' ? '#ef4444' : "#94a3b8"}
                                                    fill={comment.userFeedback === 'downvote' ? '#ef4444' : 'none'}
                                                />
                                            </button>
                                        </div>

                                        {feedbackFormCommentId === comment.commentId && (
                                            <div style={{ marginTop: '1rem' }}>
                                                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.75rem' }}>
                                                    Why wasn't this helpful?
                                                </div>
                                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                                                    {feedbackReasons.map(reason => (
                                                        <button
                                                            key={reason}
                                                            onClick={() => setFeedbackReason(reason === feedbackReason ? null : reason)}
                                                            style={{
                                                                padding: '0.35rem 0.75rem',
                                                                borderRadius: '9999px',
                                                                fontSize: '0.8rem',
                                                                border: `1px solid ${feedbackReason === reason ? '#0f172a' : '#e2e8f0'}`,
                                                                backgroundColor: feedbackReason === reason ? '#f1f5f9' : 'white',
                                                                color: '#1e293b',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s'
                                                            }}
                                                        >
                                                            {reason}
                                                        </button>
                                                    ))}
                                                </div>

                                                <div style={{ marginBottom: '1rem' }}>
                                                    <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                                                        ADD A QUICK NOTE (OPTIONAL)
                                                    </div>
                                                    <textarea
                                                        value={feedbackNote}
                                                        onChange={(e) => setFeedbackNote(e.target.value.slice(0, 100))}
                                                        placeholder="Type your note here..."
                                                        style={{
                                                            width: '100%',
                                                            padding: '0.5rem',
                                                            borderRadius: '6px',
                                                            border: '1px solid #e2e8f0',
                                                            fontSize: '0.875rem',
                                                            minHeight: '60px',
                                                            fontFamily: 'inherit',
                                                            resize: 'vertical'
                                                        }}
                                                    />
                                                    <div style={{ textAlign: 'right', fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                                                        {100 - feedbackNote.length} characters max
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                    <button
                                                        onClick={() => handleFeedbackReasonSubmit(comment.commentId)}
                                                        style={{
                                                            backgroundColor: 'black',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '9999px',
                                                            padding: '0.4rem 1.25rem',
                                                            fontSize: '0.875rem',
                                                            fontWeight: 600,
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Submit
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {feedbackSuccess[comment.commentId] && (
                                            <div style={{ marginTop: '0.75rem', fontSize: '0.9rem', fontWeight: 700, color: '#0f172a' }}>
                                                Thank you for your feedback!
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Recursively render children */}
                {comment.children && comment.children.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '1.5rem', marginLeft: '1rem' }}>
                        {comment.children.map((child: any) => {
                            // SKIP rendering this child if it's the brand reply and we are utilizing it
                            // as the "Replies" block for the current comment.
                            if (child === brandReplyChild) {
                                return null;
                            }
                            return renderComment(child, depth + 1);
                        })}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="page-container">
            {/* Tabs */}
            <div className="tabs-container" style={{ display: 'flex', gap: '2rem', marginBottom: '2rem', borderBottom: '1px solid #e2e8f0' }}>
                {tabs.map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '0.75rem 0',
                            fontSize: '0.95rem',
                            fontWeight: activeTab === tab ? 600 : 400,
                            color: activeTab === tab ? '#2563eb' : '#64748b',
                            borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Search and Filters */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                {/* Search for User Comments Only */}
                {activeTab === 'User Comments' && (
                    <div className="search-bar" style={{ flex: '1 1 200px', maxWidth: '300px', minWidth: '150px' }}>
                        <Search size={18} color="#94a3b8" />
                        <input
                            type="text"
                            placeholder="Search Comments or Posts"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                    </div>
                )}

                {/* Filters for User Comments Only */}
                {activeTab === 'User Comments' && (
                    <div style={{ display: 'flex', gap: '0.5rem', flex: '1 1 auto', justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
                        {/* Workload Dropdown with Count */}
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <button
                                onClick={(e) => { e.stopPropagation(); setShowWorkloadDropdown(!showWorkloadDropdown); setShowPostDropdown(false); setShowSentimentDropdown(false); }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    padding: '0.4rem 0.5rem',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    backgroundColor: 'white',
                                    color: '#0f172a',
                                    cursor: 'pointer',
                                    minWidth: '90px',
                                    justifyContent: 'space-between',
                                    fontSize: '0.8rem'
                                }}
                            >
                                <span>{filterWorkload}</span>
                                <ChevronDown size={16} />
                            </button>

                            {/* Count Display next to Workload */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '0.25rem 0.75rem',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                whiteSpace: 'nowrap'
                            }}>
                                {pendingCount} / {totalCount}
                            </div>

                            {showWorkloadDropdown && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    marginTop: '0.5rem',
                                    backgroundColor: 'white',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    zIndex: 50,
                                    width: '100%'
                                }}>
                                    <div
                                        onClick={() => { setFilterWorkload('My Work'); setShowWorkloadDropdown(false); }}
                                        style={{ padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9' }}
                                    >
                                        My Work
                                    </div>
                                    <div
                                        onClick={() => { setFilterWorkload('Unassigned'); setShowWorkloadDropdown(false); }}
                                        style={{ padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9' }}
                                    >
                                        Unassigned
                                    </div>
                                    <div
                                        onClick={() => { setFilterWorkload('Assigned'); setShowWorkloadDropdown(false); }}
                                        style={{ padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9' }}
                                    >
                                        Assigned
                                    </div>
                                    <div
                                        onClick={() => { setFilterWorkload('All Work'); setShowWorkloadDropdown(false); }}
                                        style={{ padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.875rem' }}
                                    >
                                        All Work
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Date Range Filters for User Comments */}
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <input
                                    type="date"
                                    value={filterStartDate}
                                    onChange={(e) => setFilterStartDate(e.target.value)}
                                    style={{
                                        padding: '0.4rem 0.5rem',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        fontSize: '0.8rem',
                                        color: '#1f2937',
                                        backgroundColor: 'white',
                                        cursor: 'pointer',
                                        outline: 'none',
                                        fontFamily: 'inherit',
                                        transition: 'border-color 0.2s',
                                        width: '115px',
                                        height: '32px'
                                    }}
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <input
                                    type="date"
                                    value={filterEndDate}
                                    onChange={(e) => setFilterEndDate(e.target.value)}
                                    min={filterStartDate}
                                    style={{
                                        padding: '0.4rem 0.5rem',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        fontSize: '0.8rem',
                                        color: '#1f2937',
                                        backgroundColor: 'white',
                                        cursor: 'pointer',
                                        outline: 'none',
                                        fontFamily: 'inherit',
                                        transition: 'border-color 0.2s',
                                        width: '115px',
                                        height: '32px'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Filter Post Dropdown */}
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={(e) => { e.stopPropagation(); setShowPostDropdown(!showPostDropdown); setShowSentimentDropdown(false); setShowWorkloadDropdown(false); }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    padding: '0.4rem 0.5rem',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    backgroundColor: 'white',
                                    color: filterPostId ? '#0f172a' : '#64748b',
                                    cursor: 'pointer',
                                    minWidth: '140px',
                                    justifyContent: 'space-between',
                                    fontSize: '0.8rem'
                                }}
                            >
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' }}>
                                    {filterPostId ? (postOptions.find(p => p.postId === filterPostId)?.postBody?.substring(0, 20) + '...' || 'Selected Post') : 'Filter Post'}
                                </span>
                                <ChevronDown size={16} />
                            </button>

                            {showPostDropdown && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    marginTop: '0.5rem',
                                    backgroundColor: 'white',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    zIndex: 50,
                                    width: '300px',
                                    maxHeight: '300px',
                                    overflowY: 'auto'
                                }}>
                                    <div
                                        onClick={() => { setFilterPostId(''); setShowPostDropdown(false); }}
                                        style={{ padding: '0.75rem 1rem', cursor: 'pointer', color: '#64748b', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9' }}
                                    >
                                        All Posts
                                    </div>
                                    {postOptions.map(post => (
                                        <div
                                            key={post.postId}
                                            onClick={() => { setFilterPostId(post.postId); setShowPostDropdown(false); }}
                                            style={{
                                                padding: '0.75rem 1rem',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                gap: '0.75rem',
                                                alignItems: 'center',
                                                backgroundColor: filterPostId === post.postId ? '#f8fafc' : 'white',
                                                borderBottom: '1px solid #f1f5f9'
                                            }}
                                        >
                                            {post.imageUrl ? (
                                                <img src={post.imageUrl} alt="" style={{ width: '32px', height: '32px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }} />
                                            ) : (
                                                <div style={{ width: '32px', height: '32px', borderRadius: '4px', backgroundColor: '#e2e8f0', flexShrink: 0 }} />
                                            )}
                                            <div style={{ fontSize: '0.8rem', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {post.postBody || 'No text'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Sentiment Dropdown */}
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={(e) => { e.stopPropagation(); setShowSentimentDropdown(!showSentimentDropdown); setShowPostDropdown(false); setShowWorkloadDropdown(false); }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    padding: '0.4rem 0.5rem',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    backgroundColor: 'white',
                                    color: filterSentiment ? '#0f172a' : '#64748b',
                                    cursor: 'pointer',
                                    minWidth: '100px',
                                    justifyContent: 'space-between',
                                    fontSize: '0.8rem'
                                }}
                            >
                                <span>
                                    {filterSentiment ? (filterSentiment.charAt(0) + filterSentiment.slice(1).toLowerCase()) : 'All'}
                                </span>
                                <ChevronDown size={16} />
                            </button>

                            {showSentimentDropdown && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    marginTop: '0.25rem',
                                    backgroundColor: 'white',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '0.5rem',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    zIndex: 1000,
                                    minWidth: '160px'
                                }}>
                                    <div
                                        onClick={() => { setFilterSentiment(''); setShowSentimentDropdown(false); }}
                                        style={{ padding: '0.5rem 1rem', cursor: 'pointer', color: '#64748b', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9' }}
                                    >
                                        All
                                    </div>
                                    <div
                                        onClick={() => { setFilterSentiment('POSITIVE'); setShowSentimentDropdown(false); }}
                                        style={{ padding: '0.5rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9' }}
                                    >
                                        <Smile size={16} color="#4CAF50" /> Positive
                                    </div>
                                    <div
                                        onClick={() => { setFilterSentiment('NEGATIVE'); setShowSentimentDropdown(false); }}
                                        style={{ padding: '0.5rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9' }}
                                    >
                                        <Frown size={16} color="#F44336" /> Negative
                                    </div>
                                    <div
                                        onClick={() => { setFilterSentiment('NEUTRAL'); setShowSentimentDropdown(false); }}
                                        style={{ padding: '0.5rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}
                                    >
                                        <Meh size={16} color="#9C27B0" /> Neutral
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            className="icon-btn"
                            style={{ border: '1px solid #e2e8f0', padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => setShowFilterModal(true)}
                        >
                            <Filter size={18} color="#64748b" />
                        </button>
                        <button
                            className="icon-btn"
                            style={{ border: '1px solid #e2e8f0', padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => {
                                fetchComments();
                            }}
                        >
                            <RefreshCw size={18} color="#64748b" />
                        </button>
                    </div>
                )}

                {/* Search and Filters for All Posts Only */}
                {activeTab === 'All Posts' && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <div style={{ position: 'relative', width: '300px' }}>
                            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
                            <input
                                type="text"
                                placeholder="Search Posts"
                                value={postsSearchQuery}
                                onChange={(e) => setPostsSearchQuery(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        setPostsPage(1);
                                        fetchAllPosts();
                                    }
                                }}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem 0.75rem 0.5rem 2.5rem',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    outline: 'none',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                            />
                        </div>
                    </div>
                )}

                {/* Filter and Refresh buttons for All Posts - Right aligned */}
                {activeTab === 'All Posts' && (
                    <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
                        <button
                            className="icon-btn"
                            style={{ border: '1px solid #e2e8f0', padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => setShowFilterModal(true)}
                        >
                            <Filter size={18} color="#64748b" />
                        </button>
                        <button
                            className="icon-btn"
                            style={{ border: '1px solid #e2e8f0', padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => {
                                setPostsPage(1);
                                setPostsSearchQuery('');
                                fetchAllPosts();
                            }}
                        >
                            <RefreshCw size={18} color="#64748b" />
                        </button>
                    </div>
                )}

                {activeTab === 'Statistics' && (
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', justifyContent: 'flex-end', width: '100%' }}>
                        <div style={{ marginRight: 'auto' }}></div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                            <label style={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: '#64748b',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                paddingLeft: '0.25rem'
                            }}>
                                Assignment Scope
                            </label>
                            <select
                                value={assignmentScope}
                                onChange={(e) => setAssignmentScope(e.target.value as 'My Work' | 'All Work')}
                                style={{
                                    padding: '0.5rem 0.75rem',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    color: '#1e293b',
                                    backgroundColor: 'white',
                                    cursor: 'pointer',
                                    outline: 'none',
                                    fontFamily: 'inherit',
                                    width: '180px',
                                    height: '40px'
                                }}
                            >
                                <option value="My Work">My Work</option>
                                <option value="All Work">All Work</option>
                            </select>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                            <label style={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: '#64748b',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                paddingLeft: '0.25rem'
                            }}>
                                Start Date
                            </label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                style={{
                                    padding: '0.5rem 0.75rem',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    color: '#1e293b',
                                    backgroundColor: 'white',
                                    cursor: 'pointer',
                                    outline: 'none',
                                    fontFamily: 'inherit',
                                    transition: 'border-color 0.2s',
                                    width: '180px',
                                    height: '40px'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                            <label style={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: '#64748b',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                paddingLeft: '0.25rem'
                            }}>
                                End Date
                            </label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                style={{
                                    padding: '0.5rem 0.75rem',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    color: '#1e293b',
                                    backgroundColor: 'white',
                                    cursor: 'pointer',
                                    outline: 'none',
                                    fontFamily: 'inherit',
                                    transition: 'border-color 0.2s',
                                    width: '180px',
                                    height: '40px'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Tab Content */}
            {activeTab === 'User Comments' && (
                <>
                    {/* Table */}
                    {loading ? (
                        <div className="loading-state">Loading AI responses...</div>
                    ) : (
                        <div className="table-container" style={{ backgroundColor: '#f8fafc', boxShadow: 'none', border: 'none' }}>
                            <table className="data-table" style={{ backgroundColor: 'transparent' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f1f5f9', borderBottom: 'none' }}>
                                        <th style={{ width: '120px', borderRadius: '8px 0 0 0' }}>ASSIGNEE</th>
                                        <th style={{ width: '60px' }}>POST MEDIA</th>
                                        <th style={{ width: '20%' }}>POST</th>
                                        <th style={{ width: '20%' }}>COMMENT</th>
                                        <th style={{ width: '20%' }}>AI RESPONSE</th>
                                        <th style={{ width: '15%' }}>POSTED RESPONSE</th>
                                        <th style={{ width: '80px' }}></th>
                                        <th style={{ width: '100px', borderRadius: '0 8px 8px 0' }}></th>
                                    </tr>
                                </thead>
                                <tbody style={{ borderSpacing: '0 10px' }}>
                                    {comments.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                                                No comments found
                                            </td>
                                        </tr>
                                    ) : (
                                        comments.map((comment) => (
                                            <tr
                                                key={comment.commentId}
                                                style={{ backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px', cursor: 'pointer' }}
                                                onClick={() => handleRowClick(comment)}
                                            >
                                                <td style={{ borderRadius: '8px 0 0 0', verticalAlign: 'middle', textAlign: 'center', padding: '0.5rem' }}>
                                                    <div
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedCommentForAssignment(comment.commentId);
                                                            fetchAssignmentUsers();
                                                            setShowAssignmentModal(true);
                                                        }}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'pointer',
                                                            minHeight: '32px'
                                                        }}
                                                        title={comment.assignedUser ? `Assigned to ${comment.assignedUser.name || comment.assignedUser.username}` : "Click to assign"}
                                                    >
                                                        {comment.assignedUser ? (
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '0.5rem',
                                                                padding: '0.25rem 0.5rem',
                                                                backgroundColor: '#f0f9ff',
                                                                border: '1px solid #0ea5e9',
                                                                borderRadius: '16px',
                                                                fontSize: '0.75rem',
                                                                color: '#0369a1'
                                                            }}>
                                                                <div style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: '#0ea5e9',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: 'white',
                                                                    fontSize: '0.6rem',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    {(comment.assignedUser.name || comment.assignedUser.username)?.charAt(0).toUpperCase()}
                                                                </div>
                                                                <span style={{ fontWeight: '500' }}>
                                                                    {comment.assignedUser.name || comment.assignedUser.username}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <div style={{
                                                                width: '32px',
                                                                height: '32px',
                                                                borderRadius: '50%',
                                                                border: '2px dashed #d1d5db',
                                                                backgroundColor: '#f9fafb',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '0.75rem',
                                                                color: '#9ca3af'
                                                            }}>
                                                                👤
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    {comment.post.imageUrl ? (
                                                        <img
                                                            src={comment.post.imageUrl}
                                                            alt="Post"
                                                            style={{
                                                                width: '48px',
                                                                height: '48px',
                                                                objectFit: 'cover',
                                                                borderRadius: '8px'
                                                            }}
                                                        />
                                                    ) : (
                                                        <div style={{
                                                            width: '48px',
                                                            height: '48px',
                                                            backgroundColor: '#1a1a1a',
                                                            borderRadius: '8px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <span style={{ color: 'white', fontSize: '0.6rem' }}>No Img</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    <div style={{ fontSize: '0.875rem', color: '#1e293b' }}>
                                                        {truncateText(comment.post.postBody || comment.post.postDescription)}
                                                    </div>
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    <div style={{ fontSize: '0.875rem', color: '#1e293b' }}>
                                                        {truncateText(comment.commentBody)}
                                                    </div>
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    <div style={{ fontSize: '0.875rem', color: '#1e293b' }}>
                                                        {comment.aiResponse || '--'}
                                                    </div>
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                                        {comment.response || '--'}
                                                    </div>
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <button
                                                            className="icon-btn"
                                                            style={{ padding: '0.25rem' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleToggleHide(comment);
                                                            }}
                                                            title={comment.isHidden ? 'Unhide comment' : 'Hide comment'}
                                                        >
                                                            {comment.isHidden ? <EyeOff size={16} /> : <Eye size={16} color="#94a3b8" />}
                                                        </button>
                                                        <button
                                                            className="icon-btn"
                                                            style={{ padding: '0.25rem' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleToggleBookmark(comment);
                                                            }}
                                                            title={comment.isBookmarked ? 'Remove bookmark' : 'Bookmark comment'}
                                                        >
                                                            <Bookmark size={16} fill={comment.isBookmarked ? 'currentColor' : 'none'} color="#94a3b8" />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td style={{ borderRadius: '0 8px 8px 0', verticalAlign: 'middle' }}>
                                                    <button
                                                        className="btn-primary"
                                                        onClick={(e) => handlePostResponse(comment.commentId, e)}
                                                        disabled={comment.isPosted || !comment.aiResponse}
                                                        style={{
                                                            padding: '0.4rem 1.2rem',
                                                            fontSize: '0.875rem',
                                                            backgroundColor: comment.isPosted ? '#94a3b8' : '#2563eb',
                                                            cursor: comment.isPosted ? 'default' : 'pointer',
                                                            borderRadius: '6px',
                                                            border: 'none',
                                                            color: 'white',
                                                            fontWeight: 500
                                                        }}
                                                    >
                                                        {comment.isPosted ? 'Posted' : 'Post'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            {!loading && total > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', flexWrap: 'nowrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '1.5rem', flexShrink: 0 }}>
                                        <span style={{ fontSize: '0.875rem', color: '#64748b', whiteSpace: 'nowrap' }}>Rows per page:</span>
                                        <select
                                            value={limit}
                                            onChange={(e) => setLimit(Number(e.target.value))}
                                            style={{
                                                padding: '0.25rem 0.5rem',
                                                borderRadius: '4px',
                                                border: '1px solid #e2e8f0',
                                                fontSize: '0.875rem',
                                                color: '#475569',
                                                backgroundColor: 'white',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                    </div>
                                    <span style={{ fontSize: '0.875rem', color: '#64748b', marginRight: '1rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                        Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} results
                                    </span>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            disabled={page === 1}
                                            className="icon-btn"
                                            style={{
                                                padding: '0.5rem',
                                                borderRadius: '6px',
                                                border: '1px solid #e2e8f0',
                                                cursor: page === 1 ? 'not-allowed' : 'pointer',
                                                opacity: page === 1 ? 0.5 : 1
                                            }}
                                        >
                                            <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>‹</span>
                                        </button>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                            <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1e293b', padding: '0 0.5rem' }}>
                                                Page {page} of {Math.ceil(total / limit)}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => setPage(p => Math.min(Math.ceil(total / limit), p + 1))}
                                            disabled={page >= Math.ceil(total / limit)}
                                            className="icon-btn"
                                            style={{
                                                padding: '0.5rem',
                                                borderRadius: '6px',
                                                border: '1px solid #e2e8f0',
                                                cursor: page >= Math.ceil(total / limit) ? 'not-allowed' : 'pointer',
                                                opacity: page >= Math.ceil(total / limit) ? 0.5 : 1
                                            }}
                                        >
                                            <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>›</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {activeTab === 'All Posts' && (
                <>
                    {postsLoading ? (
                        <div className="loading-state">Loading posts...</div>
                    ) : (
                        <div className="table-container" style={{ backgroundColor: '#f8fafc', boxShadow: 'none', border: 'none' }}>
                            <table className="data-table" style={{ backgroundColor: 'transparent' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f1f5f9', borderBottom: 'none' }}>
                                        <th style={{ width: '80px', borderRadius: '8px 0 0 8px' }}>Post Media</th>
                                        <th style={{ width: '50%' }}>Post Copy</th>
                                        <th style={{ width: '15%' }}>Posted Date</th>
                                        <th style={{ width: '120px', borderRadius: '0 8px 8px 0' }}>Reply Rate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allPosts.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                                                No posts found
                                            </td>
                                        </tr>
                                    ) : (
                                        allPosts.map((post) => {
                                            return (
                                                <tr
                                                    key={post.postId}
                                                    style={{ backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px', cursor: 'pointer' }}
                                                    onClick={() => handlePostClick(post)}
                                                >
                                                    <td style={{ borderRadius: '8px 0 0 8px', verticalAlign: 'middle' }}>
                                                        {post.imageUrl ? (
                                                            <img
                                                                src={post.imageUrl}
                                                                alt="Post"
                                                                style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px' }}
                                                            />
                                                        ) : (
                                                            <div style={{ width: '48px', height: '48px', backgroundColor: '#e2e8f0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <span style={{ color: '#94a3b8', fontSize: '0.6rem' }}>No Img</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        <div style={{ fontSize: '0.875rem', color: '#1e293b', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }}>
                                                            {post.postBody || '--'}
                                                        </div>
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                                            {post.postedAt ? formatFullTimestamp(post.postedAt) : '--'}
                                                        </div>
                                                    </td>
                                                    <td style={{ borderRadius: '0 8px 8px 0', verticalAlign: 'middle' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <div style={{ position: 'relative', width: '48px', height: '48px' }}>
                                                                <svg width="48" height="48" viewBox="0 0 48 48">
                                                                    <circle cx="24" cy="24" r="20" fill="none" stroke="#e2e8f0" strokeWidth="4" />
                                                                    <circle
                                                                        cx="24"
                                                                        cy="24"
                                                                        r="20"
                                                                        fill="none"
                                                                        stroke="#3b82f6"
                                                                        strokeWidth="4"
                                                                        strokeDasharray={`${post.replyRate * 1.256} 125.6`}
                                                                        strokeLinecap="round"
                                                                        transform="rotate(-90 24 24)"
                                                                    />
                                                                </svg>
                                                                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '0.7rem', fontWeight: 600, color: '#1e293b' }}>
                                                                    {post.replyRate}%
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            {postsTotal > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', flexWrap: 'nowrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '1.5rem', flexShrink: 0 }}>
                                        <span style={{ fontSize: '0.875rem', color: '#64748b', whiteSpace: 'nowrap' }}>Rows per page:</span>
                                        <select
                                            value={postsLimit}
                                            onChange={(e) => { setPostsLimit(Number(e.target.value)); setPostsPage(1); }}
                                            style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.875rem', color: '#475569', backgroundColor: 'white', cursor: 'pointer' }}
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                        </select>
                                    </div>
                                    <span style={{ fontSize: '0.875rem', color: '#64748b', marginRight: '1rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                        {(postsPage - 1) * postsLimit + 1}-{Math.min(postsPage * postsLimit, postsTotal)} of {postsTotal}
                                    </span>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button
                                            onClick={() => setPostsPage(p => Math.max(1, p - 1))}
                                            disabled={postsPage === 1}
                                            className="icon-btn"
                                            style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', cursor: postsPage === 1 ? 'not-allowed' : 'pointer', opacity: postsPage === 1 ? 0.5 : 1 }}
                                        >
                                            <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>‹</span>
                                        </button>
                                        <button
                                            onClick={() => setPostsPage(p => Math.min(Math.ceil(postsTotal / postsLimit), p + 1))}
                                            disabled={postsPage >= Math.ceil(postsTotal / postsLimit)}
                                            className="icon-btn"
                                            style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', cursor: postsPage >= Math.ceil(postsTotal / postsLimit) ? 'not-allowed' : 'pointer', opacity: postsPage >= Math.ceil(postsTotal / postsLimit) ? 0.5 : 1 }}
                                        >
                                            <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>›</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {activeTab === 'Tagged Posts' && (
                <>
                    {taggedPostsLoading ? (
                        <div className="loading-state">Loading tagged posts...</div>
                    ) : (
                        <div className="table-container" style={{ backgroundColor: '#f8fafc', boxShadow: 'none', border: 'none' }}>
                            <table className="data-table" style={{ backgroundColor: 'transparent' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f1f5f9', borderBottom: 'none' }}>
                                        <th style={{ width: '80px', borderRadius: '8px 0 0 8px' }}>Post Media</th>
                                        <th style={{ width: '50%' }}>Post Copy</th>
                                        <th style={{ width: '15%' }}>Posted At</th>
                                        <th style={{ width: '120px', borderRadius: '0 8px 8px 0' }}>Platform</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {taggedPosts.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                                                No tagged posts found
                                            </td>
                                        </tr>
                                    ) : (
                                        taggedPosts.map((post) => {
                                            return (
                                                <tr
                                                    key={post.postId}
                                                    style={{ backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px', cursor: 'pointer' }}
                                                    onClick={() => handlePostClick(post)}
                                                >
                                                    <td style={{ borderRadius: '8px 0 0 8px', verticalAlign: 'middle' }}>
                                                        {post.imageUrl ? (
                                                            <img
                                                                src={post.imageUrl}
                                                                alt="Post"
                                                                style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px' }}
                                                            />
                                                        ) : (
                                                            <div style={{ width: '48px', height: '48px', backgroundColor: '#e2e8f0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <span style={{ color: '#94a3b8', fontSize: '0.6rem' }}>No Img</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        <div style={{ fontSize: '0.875rem', color: '#1e293b', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }}>
                                                            {post.postBody || '--'}
                                                        </div>
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                                            {post.postedAt ? formatFullTimestamp(post.postedAt) : '--'}
                                                        </div>
                                                    </td>
                                                    <td style={{ borderRadius: '0 8px 8px 0', verticalAlign: 'middle' }}>
                                                        <div style={{ fontSize: '0.875rem', color: '#4F46E5', fontWeight: '500' }}>
                                                            Instagram Tag
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            {taggedPostsTotal > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', flexWrap: 'nowrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '1.5rem', flexShrink: 0 }}>
                                        <span style={{ fontSize: '0.875rem', color: '#64748b', whiteSpace: 'nowrap' }}>Rows per page:</span>
                                        <select
                                            value={taggedPostsLimit}
                                            onChange={(e) => { setTaggedPostsLimit(Number(e.target.value)); setTaggedPostsPage(1); }}
                                            style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.875rem', color: '#475569', backgroundColor: 'white', cursor: 'pointer' }}
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                        </select>
                                    </div>
                                    <span style={{ fontSize: '0.875rem', color: '#64748b', marginRight: '1rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                        {(taggedPostsPage - 1) * taggedPostsLimit + 1}-{Math.min(taggedPostsPage * taggedPostsLimit, taggedPostsTotal)} of {taggedPostsTotal}
                                    </span>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button
                                            onClick={() => setTaggedPostsPage(p => Math.max(1, p - 1))}
                                            disabled={taggedPostsPage === 1}
                                            className="icon-btn"
                                            style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', cursor: taggedPostsPage === 1 ? 'not-allowed' : 'pointer', opacity: taggedPostsPage === 1 ? 0.5 : 1 }}
                                        >
                                            <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>‹</span>
                                        </button>
                                        <button
                                            onClick={() => setTaggedPostsPage(p => Math.min(Math.ceil(taggedPostsTotal / taggedPostsLimit), p + 1))}
                                            disabled={taggedPostsPage >= Math.ceil(taggedPostsTotal / taggedPostsLimit)}
                                            className="icon-btn"
                                            style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', cursor: taggedPostsPage >= Math.ceil(taggedPostsTotal / taggedPostsLimit) ? 'not-allowed' : 'pointer', opacity: taggedPostsPage >= Math.ceil(taggedPostsTotal / taggedPostsLimit) ? 0.5 : 1 }}
                                        >
                                            <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>›</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {activeTab === 'Statistics' && (
                <div style={{ padding: '0' }}>
                    {statisticsLoading ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '4rem',
                            color: '#64748b'
                        }}>
                            <RefreshCw size={32} className="spin" style={{ marginBottom: '1rem' }} />
                            <p style={{ fontSize: '1rem', fontWeight: 500 }}>Loading statistics...</p>
                        </div>
                    ) : statisticsData ? (
                        <>
                            {/* Two Column Grid */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '1.5rem',
                                marginBottom: '2rem'
                            }}>
                                {/* Posts & Comments */}
                                <div style={{
                                    background: 'white',
                                    borderRadius: '16px',
                                    padding: '2rem',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <h2 style={{
                                        fontSize: '1.25rem',
                                        fontWeight: 600,
                                        color: '#0f172a',
                                        marginBottom: '2rem'
                                    }}>
                                        Posts & Comments
                                    </h2>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem 3rem' }}>
                                        <div>
                                            <div style={{ fontSize: '0.875rem', fontWeight: 400, color: '#64748b', marginBottom: '0.5rem' }}>Your Posts</div>
                                            <div style={{ fontSize: '2.5rem', fontWeight: 400, color: '#0f172a' }}>{statisticsData.yourPosts}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.875rem', fontWeight: 400, color: '#64748b', marginBottom: '0.5rem' }}>Total Comments</div>
                                            <div style={{ fontSize: '2.5rem', fontWeight: 400, color: '#0f172a' }}>{statisticsData.totalComments}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.875rem', fontWeight: 400, color: '#64748b', marginBottom: '0.5rem' }}>Total Respondable Comments</div>
                                            <div style={{ fontSize: '2.5rem', fontWeight: 400, color: '#0f172a' }}>{statisticsData.totalRespondableComments}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.875rem', fontWeight: 400, color: '#64748b', marginBottom: '0.5rem' }}>Hidden Comments</div>
                                            <div style={{ fontSize: '2.5rem', fontWeight: 400, color: '#0f172a' }}>{statisticsData.hiddenComments}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Comment Responses */}
                                <div style={{
                                    background: 'white',
                                    borderRadius: '16px',
                                    padding: '2rem',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <h2 style={{
                                        fontSize: '1.25rem',
                                        fontWeight: 600,
                                        color: '#0f172a',
                                        marginBottom: '2rem'
                                    }}>
                                        Comment Responses
                                    </h2>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem 3rem' }}>
                                        <div>
                                            <div style={{ fontSize: '0.875rem', fontWeight: 400, color: '#64748b', marginBottom: '0.5rem' }}>Responded</div>
                                            <div style={{ fontSize: '2.5rem', fontWeight: 400, color: '#0f172a' }}>{statisticsData.responded}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.875rem', fontWeight: 400, color: '#64748b', marginBottom: '0.5rem' }}>Response Pending</div>
                                            <div style={{ fontSize: '2.5rem', fontWeight: 400, color: '#0f172a' }}>{statisticsData.responsePending}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.875rem', fontWeight: 400, color: '#64748b', marginBottom: '0.5rem' }}>Responded from Portal</div>
                                            <div style={{ fontSize: '2.5rem', fontWeight: 400, color: '#0f172a' }}>{statisticsData.respondedFromPortal}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.875rem', fontWeight: 400, color: '#64748b', marginBottom: '0.5rem' }}>
                                                Responded from {platformFilter?.toUpperCase() === 'FACEBOOK' ? 'Facebook' : platformFilter?.toUpperCase() === 'TIKTOK' ? 'TikTok' : platformFilter?.toUpperCase() === 'TWITTER' ? 'X' : 'Instagram'}
                                            </div>
                                            <div style={{ fontSize: '2.5rem', fontWeight: 400, color: '#0f172a' }}>
                                                {platformFilter?.toUpperCase() === 'FACEBOOK' ? statisticsData.respondedFromFacebook : statisticsData.respondedFromInstagram}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Response Rate Gauge */}
                            <div style={{
                                background: 'white',
                                borderRadius: '16px',
                                padding: '2rem',
                                border: '1px solid #e2e8f0',
                                textAlign: 'center'
                            }}>
                                <h2 style={{
                                    fontSize: '1.125rem',
                                    fontWeight: 600,
                                    color: '#0f172a',
                                    marginBottom: '1.5rem'
                                }}>
                                    Response Rate
                                </h2>
                                <div style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    width: '350px',
                                    height: '180px',
                                    marginTop: '1rem'
                                }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <RechartsPieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                                            <Pie
                                                data={[
                                                    { value: statisticsData.responseRate },
                                                    { value: 100 - statisticsData.responseRate }
                                                ]}
                                                cx="50%"
                                                cy="100%"
                                                startAngle={180}
                                                endAngle={0}
                                                innerRadius={100}
                                                outerRadius={130}
                                                dataKey="value"
                                                stroke="none"
                                                paddingAngle={0}
                                            >
                                                <Cell fill="#4285f4" />
                                                <Cell fill="#e2e8f0" />
                                            </Pie>
                                        </RechartsPieChart>
                                    </ResponsiveContainer>
                                    <div style={{
                                        position: 'absolute',
                                        bottom: '10px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        fontSize: '3.5rem',
                                        fontWeight: 700,
                                        color: '#0f172a',
                                        lineHeight: 1
                                    }}>
                                        {statisticsData.responseRate}%
                                    </div>
                                </div>
                            </div>

                            {/* Assignment Report Table */}
                            <div style={{
                                background: 'white',
                                borderRadius: '16px',
                                padding: '2rem',
                                border: '1px solid #e2e8f0',
                                marginTop: '2rem',
                                overflowX: 'auto'
                            }}>
                                <h2 style={{
                                    fontSize: '1.25rem',
                                    fontWeight: 600,
                                    color: '#0f172a',
                                    marginBottom: '1.5rem'
                                }}>
                                    Assignment Report
                                </h2>
                                {assignmentReportLoading ? (
                                    <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                                        <RefreshCw size={24} className="spin" style={{ marginBottom: '1rem' }} />
                                    </div>
                                ) : assignmentReport.length > 0 ? (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '800px' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '0.875rem' }}>
                                                <th style={{ padding: '1rem 0.5rem', width: '50px' }}>Post</th>
                                                <th style={{ padding: '1rem 0.5rem', width: '30%' }}>Comment</th>
                                                <th style={{ padding: '1rem 0.5rem' }}>Status</th>
                                                <th style={{ padding: '1rem 0.5rem' }}>Assigned To</th>
                                                <th style={{ padding: '1rem 0.5rem' }}>Completed By</th>
                                                <th style={{ padding: '1rem 0.5rem' }}>Assigned At</th>
                                                <th style={{ padding: '1rem 0.5rem' }}>Completed At</th>
                                                <th style={{ padding: '1rem 0.5rem' }}>Duration</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assignmentReport.map((row: any) => (
                                                <tr key={row.id} style={{ borderBottom: '1px solid #e2e8f0', fontSize: '0.875rem', backgroundColor: row.differs ? '#fef2f2' : 'transparent' }}>
                                                    <td style={{ padding: '1rem 0.5rem' }}>
                                                        {row.postMediaUrl ? (
                                                            <div style={{
                                                                width: '32px',
                                                                height: '32px',
                                                                borderRadius: '4px',
                                                                overflow: 'hidden',
                                                                backgroundColor: '#f1f5f9',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}>
                                                                {row.postMediaUrl.endsWith('.mp4') || row.postMediaUrl.includes('video') ? (
                                                                    <div style={{ backgroundColor: '#e2e8f0', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#64748b', fontWeight: 'bold' }}>VID</div>
                                                                ) : (
                                                                    <img src={row.postMediaUrl} alt="Post Thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div style={{ width: '32px', height: '32px', borderRadius: '4px', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <span style={{ fontSize: '10px', color: '#94a3b8' }}>-</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '1rem 0.5rem', color: '#1e293b' }}>
                                                        <div style={{
                                                            maxWidth: '300px',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }} title={row.commentBody}>
                                                            {row.commentBody}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '1rem 0.5rem', fontWeight: 500, color: row.status === 'COMPLETED' ? '#10b981' : (row.status === 'IN_PROGRESS' ? '#3b82f6' : '#f59e0b') }}>
                                                        {row.status?.replace('_', ' ')}
                                                    </td>
                                                    <td style={{ padding: '1rem 0.5rem', fontWeight: 500 }}>{row.assignedToUser}</td>
                                                    <td style={{ padding: '1rem 0.5rem', fontWeight: 500, color: row.differs && row.completedByUser !== '-' ? '#ef4444' : 'inherit' }}>{row.completedByUser}</td>
                                                    <td style={{ padding: '1rem 0.5rem', color: '#64748b' }}>{row.assignedAt ? new Date(row.assignedAt).toLocaleString() : '-'}</td>
                                                    <td style={{ padding: '1rem 0.5rem', color: '#64748b' }}>{row.completedAt ? new Date(row.completedAt).toLocaleString() : 'Pending'}</td>
                                                    <td style={{ padding: '1rem 0.5rem', fontWeight: 600 }}>
                                                        {(() => {
                                                            const mins = row.durationMinutes;
                                                            if (mins < 60) return `${mins} min`;
                                                            const hours = Math.floor(mins / 60);
                                                            const remMins = mins % 60;
                                                            if (hours < 24) return `${hours} hr ${remMins} min`;
                                                            const days = Math.floor(hours / 24);
                                                            const remHours = hours % 24;
                                                            return `${days} d ${remHours} hr`;
                                                        })()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p style={{ color: '#64748b', fontSize: '0.875rem', textAlign: 'center' }}>No assignments found for this period.</p>
                                )}
                            </div>
                        </>
                    ) : (
                        <div style={{
                            padding: '4rem',
                            textAlign: 'center',
                            color: '#64748b',
                            background: 'white',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <BarChart3 size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem', color: '#0f172a' }}>No Data Available</h3>
                            <p style={{ fontSize: '0.875rem' }}>Statistics will appear here once data is available from the API.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Post Detail Modal */}
            {showModal && selectedPost && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }} onClick={handleCloseModal}>
                    <div style={{
                        backgroundColor: 'white',
                        width: '90%',
                        maxWidth: '1200px',
                        height: '85vh',
                        borderRadius: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                    }} onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div style={{
                            padding: '1rem 1.5rem',
                            borderBottom: '1px solid #e2e8f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            backgroundColor: 'white'
                        }}>
                            <div>
                                <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500, marginBottom: '0.25rem' }}>{selectedPost.post.postOwnerUsername || 'Brand'}</div>
                                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
                                    {selectedPost.post.platform?.toUpperCase() === 'TIKTOK' ? 'TikTok' :
                                        selectedPost.post.platform?.toUpperCase() === 'TWITTER' || selectedPost.post.platform?.toUpperCase() === 'X' ? 'X' :
                                            selectedPost.post.platform?.toUpperCase() === 'FACEBOOK' ? 'Facebook' :
                                                selectedPost.post.platform === 'Instagram Tag' ? 'Instagram Tag' : 'Instagram'} Post
                                </h2>
                            </div>
                            <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                <X size={24} color="#64748b" />
                            </button>
                        </div>

                        {/* Body */}
                        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                            {/* Left: Image */}
                            <div style={{
                                width: '50%',
                                backgroundColor: '#f8fafc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '2rem',
                                borderRight: '1px solid #e2e8f0'
                            }}>
                                {selectedPost.post.imageUrl ? (
                                    <img
                                        src={selectedPost.post.imageUrl}
                                        alt="Post"
                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                                    />
                                ) : (
                                    <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                                        <p>No Image Available</p>
                                    </div>
                                )}
                            </div>

                            {/* Right: Content */}
                            <div style={{ width: '50%', overflowY: 'auto', padding: '2rem', backgroundColor: 'white' }}>
                                {/* Post Info */}
                                <div style={{ marginBottom: '2.5rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>Post</h3>
                                            <a href={selectedPost.post.permalink || '#'} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#64748b', textDecoration: 'none', fontSize: '0.875rem' }}>
                                                {selectedPost.post.platform?.toUpperCase() === 'TIKTOK' ? (
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                                    </svg>
                                                ) : selectedPost.post.platform?.toUpperCase() === 'TWITTER' || selectedPost.post.platform?.toUpperCase() === 'X' ? (
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                                    </svg>
                                                ) : selectedPost.post.platform?.toUpperCase() === 'FACEBOOK' ? (
                                                    <Facebook size={14} style={{ flexShrink: 0 }} />
                                                ) : (
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.645.069-4.849.069-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                                    </svg>
                                                )}
                                                <span style={{ textDecoration: 'underline' }}>View</span>
                                            </a>
                                        </div>
                                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                            {/* Toggle Switch */}
                                            <label
                                                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.5rem' }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleToggleHide(selectedPost);
                                                }}
                                            >
                                                <div style={{
                                                    position: 'relative',
                                                    width: '36px',
                                                    height: '20px',
                                                    backgroundColor: selectedPost.isHidden ? '#3b82f6' : '#e2e8f0',
                                                    borderRadius: '20px',
                                                    transition: 'background-color 0.2s'
                                                }}>
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '2px',
                                                        left: selectedPost.isHidden ? '18px' : '2px',
                                                        width: '16px',
                                                        height: '16px',
                                                        backgroundColor: 'white',
                                                        borderRadius: '50%',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                                        transition: 'left 0.2s'
                                                    }}></div>
                                                </div>
                                                <span style={{ fontSize: '0.875rem', color: '#64748b' }}>{selectedPost.isHidden ? 'Hidden' : 'Hide'}</span>
                                            </label>
                                        </div>
                                    </div>
                                    {selectedPost.postedAt && (
                                        <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>
                                            Posted: {formatFullTimestamp(selectedPost.postedAt)}
                                        </div>
                                    )}
                                    <p style={{ fontSize: '0.95rem', color: '#334155', lineHeight: '1.6', marginBottom: '1rem' }}>
                                        {selectedPost.post.postBody || selectedPost.post.postDescription}
                                    </p>
                                </div>

                                {/* Comments Section - Only show when opened from User Comments tab (has commentId) */}
                                {selectedPost.commentId && (
                                    <div>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '1.5rem', color: '#0f172a' }}>Comment</h3>

                                        {loadingComments ? (
                                            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading comments...</div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                                                {postComments.length === 0 ? (
                                                    <p style={{ color: '#64748b', textAlign: 'center' }}>No comments found.</p>
                                                ) : (
                                                    buildCommentTree(postComments).map((root: any) => renderComment(root))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filter Modal */}
            {showFilterModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '2rem',
                        maxWidth: '500px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflow: 'auto'
                    }}>
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>Filter</h2>
                            <button onClick={() => setShowFilterModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                <X size={24} />
                            </button>
                        </div>

                        <p style={{ color: '#64748b', marginBottom: '2rem' }}>Select filters below to curate a selective list of {activeTab === 'All Posts' ? 'posts' : 'user comments'}</p>

                        {/* Conditional Filters based on Active Tab */}
                        {activeTab === 'User Comments' && (
                            <>
                                {/* Post Type Filter */}
                                <div style={{ marginBottom: '2rem' }}>
                                    <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Post Type</h3>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button
                                            onClick={() => setFilterBookmarked(null)}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '9999px',
                                                border: 'none',
                                                backgroundColor: filterBookmarked === null ? '#0f172a' : '#f1f5f9',
                                                color: filterBookmarked === null ? 'white' : '#64748b',
                                                cursor: 'pointer',
                                                fontWeight: 500
                                            }}
                                        >
                                            Show All
                                        </button>
                                        <button
                                            onClick={() => setFilterBookmarked(true)}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '9999px',
                                                border: 'none',
                                                backgroundColor: filterBookmarked === true ? '#0f172a' : '#f1f5f9',
                                                color: filterBookmarked === true ? 'white' : '#64748b',
                                                cursor: 'pointer',
                                                fontWeight: 500
                                            }}
                                        >
                                            Only Bookmarks
                                        </button>
                                    </div>
                                </div>

                                {/* Reply Status Filter */}
                                <div style={{ marginBottom: '2rem' }}>
                                    <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Reply Status</h3>
                                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                        <button
                                            onClick={() => setFilterPosted('not_posted')}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '9999px',
                                                border: 'none',
                                                backgroundColor: filterPosted === 'not_posted' ? '#0f172a' : '#f1f5f9',
                                                color: filterPosted === 'not_posted' ? 'white' : '#64748b',
                                                cursor: 'pointer',
                                                fontWeight: 500
                                            }}
                                        >
                                            Not Posted
                                        </button>
                                        <button
                                            onClick={() => setFilterPosted('posted')}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '9999px',
                                                border: 'none',
                                                backgroundColor: filterPosted === 'posted' ? '#0f172a' : '#f1f5f9',
                                                color: filterPosted === 'posted' ? 'white' : '#64748b',
                                                cursor: 'pointer',
                                                fontWeight: 500
                                            }}
                                        >
                                            Posted
                                        </button>
                                        <button
                                            onClick={() => setFilterPosted('all')}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '9999px',
                                                border: 'none',
                                                backgroundColor: filterPosted === 'all' ? '#0f172a' : '#f1f5f9',
                                                color: filterPosted === 'all' ? 'white' : '#64748b',
                                                cursor: 'pointer',
                                                fontWeight: 500
                                            }}
                                        >
                                            Show All
                                        </button>
                                    </div>
                                </div>

                                {/* Show Hidden Filter */}
                                <div style={{ marginBottom: '2rem' }}>
                                    <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Show Hidden</h3>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button
                                            onClick={() => setFilterHidden(false)}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '9999px',
                                                border: 'none',
                                                backgroundColor: !filterHidden ? '#0f172a' : '#f1f5f9',
                                                color: !filterHidden ? 'white' : '#64748b',
                                                cursor: 'pointer',
                                                fontWeight: 500
                                            }}
                                        >
                                            No
                                        </button>
                                        <button
                                            onClick={() => setFilterHidden(true)}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '9999px',
                                                border: 'none',
                                                backgroundColor: filterHidden ? '#0f172a' : '#f1f5f9',
                                                color: filterHidden ? 'white' : '#64748b',
                                                cursor: 'pointer',
                                                fontWeight: 500
                                            }}
                                        >
                                            Yes
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* All Posts Filters */}
                        {activeTab === 'All Posts' && (
                            <>
                                {/* Show Advertisement Posts Filter */}
                                <div style={{ marginBottom: '2rem' }}>
                                    <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Show Advertisement Posts</h3>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button
                                            onClick={() => setShowAdvertisementPosts(true)}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '9999px',
                                                border: 'none',
                                                backgroundColor: showAdvertisementPosts ? '#0f172a' : '#f1f5f9',
                                                color: showAdvertisementPosts ? 'white' : '#64748b',
                                                cursor: 'pointer',
                                                fontWeight: 500
                                            }}
                                        >
                                            Yes
                                        </button>
                                        <button
                                            onClick={() => setShowAdvertisementPosts(false)}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '9999px',
                                                border: 'none',
                                                backgroundColor: !showAdvertisementPosts ? '#0f172a' : '#f1f5f9',
                                                color: !showAdvertisementPosts ? 'white' : '#64748b',
                                                cursor: 'pointer',
                                                fontWeight: 500
                                            }}
                                        >
                                            No
                                        </button>
                                    </div>
                                </div>

                                {/* Show Hidden Posts Filter */}
                                <div style={{ marginBottom: '2rem' }}>
                                    <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Show Hidden</h3>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button
                                            onClick={() => setShowHiddenPosts(false)}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '9999px',
                                                border: 'none',
                                                backgroundColor: !showHiddenPosts ? '#0f172a' : '#f1f5f9',
                                                color: !showHiddenPosts ? 'white' : '#64748b',
                                                cursor: 'pointer',
                                                fontWeight: 500
                                            }}
                                        >
                                            No
                                        </button>
                                        <button
                                            onClick={() => setShowHiddenPosts(true)}
                                            style={{
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '9999px',
                                                border: 'none',
                                                backgroundColor: showHiddenPosts ? '#0f172a' : '#f1f5f9',
                                                color: showHiddenPosts ? 'white' : '#64748b',
                                                cursor: 'pointer',
                                                fontWeight: 500
                                            }}
                                        >
                                            Yes
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e2e8f0' }}>
                            <button
                                onClick={() => {
                                    if (activeTab === 'User Comments') {
                                        setFilterBookmarked(null);
                                        setFilterPosted('not_posted');
                                        setFilterHidden(false);
                                    } else if (activeTab === 'All Posts') {
                                        setShowAdvertisementPosts(true);
                                        setShowHiddenPosts(false);
                                    }
                                }}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0',
                                    backgroundColor: 'white',
                                    color: '#64748b',
                                    cursor: 'pointer',
                                    fontWeight: 500
                                }}
                            >
                                Clear
                            </button>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                    onClick={() => setShowFilterModal(false)}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                        backgroundColor: 'white',
                                        color: '#64748b',
                                        cursor: 'pointer',
                                        fontWeight: 500
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        setShowFilterModal(false);
                                        if (activeTab === 'User Comments') {
                                            setPage(1);
                                            fetchComments();
                                        } else if (activeTab === 'All Posts') {
                                            setPostsPage(1);
                                            fetchAllPosts();
                                        }
                                    }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: '6px',
                                        border: 'none',
                                        backgroundColor: '#3b82f6',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontWeight: 500
                                    }}
                                >
                                    Filter
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Regenerate Modal */}
            {showRegenerateModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '2rem',
                        maxWidth: '500px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                    }}>
                        {/* Header */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1.5rem',
                            paddingBottom: '1rem',
                            borderBottom: '2px solid #3b82f6'
                        }}>
                            <h2 style={{
                                fontSize: '1.25rem',
                                fontWeight: 700,
                                color: '#0f172a',
                                margin: 0
                            }}>
                                Regenerate AI Response
                            </h2>
                            <button
                                onClick={() => {
                                    setShowRegenerateModal(false);
                                    setRegenerateCommentId(null);
                                    setCustomStyle('');
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0.25rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <X size={20} color="#64748b" />
                            </button>
                        </div>

                        {/* Style Options */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <p style={{
                                fontSize: '0.875rem',
                                color: '#64748b',
                                marginBottom: '0.5rem',
                                fontWeight: 500
                            }}>
                                Choose a style for regeneration:
                            </p>
                            <p style={{
                                fontSize: '0.8rem',
                                color: '#94a3b8',
                                marginBottom: '1rem'
                            }}>
                                Select one of the quick styles below or use a custom instruction to guide how the AI rewrites this reply.
                            </p>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '0.75rem',
                                marginBottom: '1rem'
                            }}>
                                {[
                                    { key: 'more positive', label: 'More Positive & Supportive' },
                                    { key: 'more professional', label: 'More Professional' },
                                    { key: 'more friendly', label: 'More Friendly & Casual' },
                                    { key: 'more empathetic', label: 'More Empathetic' },
                                    { key: 'shorter', label: 'Short & Direct' },
                                    { key: 'more emoji', label: 'More Fun with Emoji' }
                                ].map((option) => (
                                    <button
                                        key={option.key}
                                        onClick={() => {
                                            handleRegenerate(option.key);
                                        }}
                                        style={{
                                            padding: '0.75rem 1rem',
                                            borderRadius: '8px',
                                            border: '2px solid #e2e8f0',
                                            backgroundColor: 'white',
                                            color: '#0f172a',
                                            cursor: isRegenerating ? 'not-allowed' : 'pointer',
                                            opacity: isRegenerating ? 0.6 : 1,
                                            fontWeight: 500,
                                            fontSize: '0.875rem',
                                            textAlign: 'center',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isRegenerating) {
                                                e.currentTarget.style.borderColor = '#3b82f6';
                                                e.currentTarget.style.backgroundColor = '#eff6ff';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.backgroundColor = 'white';
                                        }}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>

                            {/* Custom Style */}
                            <div style={{ marginTop: '1rem' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#0f172a',
                                    marginBottom: '0.5rem'
                                }}>
                                    Custom Style (optional):
                                </label>
                                <input
                                    type="text"
                                    value={customStyle}
                                    onChange={(e) => setCustomStyle(e.target.value)}
                                    placeholder="e.g., more casual, more formal, add humor"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0',
                                        fontSize: '0.875rem',
                                        marginBottom: '0.75rem'
                                    }}
                                />
                                {customStyle && (
                                    <button
                                        onClick={() => handleRegenerate(customStyle)}
                                        disabled={isRegenerating}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 1rem',
                                            borderRadius: '8px',
                                            border: 'none',
                                            backgroundColor: isRegenerating ? '#93c5fd' : '#3b82f6',
                                            color: 'white',
                                            cursor: isRegenerating ? 'not-allowed' : 'pointer',
                                            fontWeight: 600,
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        {isRegenerating ? 'Generating…' : 'Regenerate with Custom Style'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '0.75rem',
                            paddingTop: '1rem',
                            borderTop: '1px solid #e2e8f0'
                        }}>
                            <button
                                onClick={() => {
                                    if (isRegenerating) return;
                                    setShowRegenerateModal(false);
                                    setRegenerateCommentId(null);
                                    setCustomStyle('');
                                }}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0',
                                    backgroundColor: 'white',
                                    color: '#64748b',
                                    cursor: isRegenerating ? 'not-allowed' : 'pointer',
                                    fontWeight: 500,
                                    opacity: isRegenerating ? 0.6 : 1
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Assignment Modal */}
            {showAssignmentModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '2rem',
                        maxWidth: '500px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflow: 'auto'
                    }}>
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>Assign to User</h2>
                            <button onClick={() => setShowAssignmentModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                <X size={24} />
                            </button>
                        </div>

                        <p style={{ color: '#64748b', marginBottom: '2rem' }}>Select a user to assign this comment to</p>

                        {/* Users List */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2rem' }}>
                            {loadingAssignmentUsers ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                                    Loading users...
                                </div>
                            ) : !Array.isArray(assignmentUsers) || assignmentUsers.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                                    No users found
                                </div>
                            ) : (
                                <>
                                    {/* Unassigned Option */}
                                    <div
                                        onClick={() => {
                                            if (assigningUser) return;
                                            handleAssignUser(); // Pass undefined to unassign
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '1rem',
                                            padding: '1rem',
                                            border: '2px solid #ef4444',
                                            borderRadius: '8px',
                                            cursor: assigningUser ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.2s',
                                            backgroundColor: '#fef2f2',
                                            opacity: assigningUser ? 0.6 : 1
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#fee2e2';
                                            e.currentTarget.style.borderColor = '#dc2626';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#fef2f2';
                                            e.currentTarget.style.borderColor = '#ef4444';
                                        }}
                                    >
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            backgroundColor: '#ef4444',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            <span style={{ fontSize: '1rem', color: 'white' }}>
                                                ✕
                                            </span>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#dc2626' }}>
                                                Unassigned
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: '#7f1d1d' }}>
                                                Remove assignment from this comment
                                            </div>
                                        </div>
                                    </div>

                                    {/* Regular Users */}
                                    {assignmentUsers.map((user) => (
                                        <div
                                            key={user.id}
                                            onClick={() => {
                                                if (assigningUser) return;
                                                handleAssignUser(user.id);
                                            }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '1rem',
                                                padding: '1rem',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                cursor: assigningUser ? 'not-allowed' : 'pointer',
                                                transition: 'all 0.2s',
                                                opacity: assigningUser ? 0.6 : 1
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#f8fafc';
                                                e.currentTarget.style.borderColor = '#3b82f6';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'white';
                                                e.currentTarget.style.borderColor = '#e2e8f0';
                                            }}
                                        >
                                            <div style={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '50%',
                                                backgroundColor: '#f3f4f6',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#6b7280' }}>
                                                    {user.name?.charAt(0).toUpperCase() || user.username?.charAt(0).toUpperCase() || '?'}
                                                </span>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>
                                                    {user.name || user.username}
                                                </div>
                                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                    {user.email}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                            <button
                                onClick={() => setShowAssignmentModal(false)}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0',
                                    backgroundColor: 'white',
                                    color: '#64748b',
                                    cursor: 'pointer',
                                    fontWeight: 500
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Regeneration Status Toast */}
            {regenToast && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: '24px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1100,
                        minWidth: '260px',
                        maxWidth: '400px',
                        padding: '0.75rem 1rem',
                        borderRadius: '9999px',
                        boxShadow: '0 10px 15px rgba(15, 23, 42, 0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        backgroundColor:
                            regenToast.type === 'error'
                                ? '#FEF2F2'
                                : regenToast.type === 'success'
                                    ? '#0F172A'
                                    : '#111827',
                        color:
                            regenToast.type === 'error'
                                ? '#991B1B'
                                : '#F9FAFB',
                        border:
                            regenToast.type === 'error'
                                ? '1px solid #FCA5A5'
                                : '1px solid rgba(148, 163, 184, 0.6)'
                    }}
                >
                    <div
                        style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '9999px',
                            backgroundColor:
                                regenToast.type === 'error'
                                    ? '#EF4444'
                                    : regenToast.type === 'success'
                                        ? '#22C55E'
                                        : '#3B82F6',
                            flexShrink: 0
                        }}
                    />
                    <span
                        style={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {regenToast.message}
                    </span>
                </div>
            )}
        </div>
    );
};

export default AIResponsePage;
